/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Building2, Check, Globe, Users, Award, FlaskConical, Shield, Clock, Target, Zap, AlertTriangle, Plus, Trash2, Search, Factory } from 'lucide-react';
import MessageModal from '../components/common/MessageModal';
import { dbService, supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import Dompurify from 'dompurify';

import {
  validateEmail,
  validateName,
  validateCompany,
  validatePhone,
  validateUrl,
  validateTextArea,
  validateEmailDomainMatch,
  validateFeiNumber,
} from '../utils/validations';
import { BATCH_SIZES_COMBINED_OPTIONS, BATCH_SIZES_KG_OPTIONS, BATCH_SIZES_L_OPTIONS, BUDGET_RANGES_OPTIONS, CDMO_BUSINESS_PROFILE_OPTIONS, CDMO_ENUM, CDMO_STATUS, type CDMOFacility, CLIENT_TYPES_OPTIONS, ENGAGEMENT_SCALES_OPTIONS, FormData, getDetailedCapabilityOptions, getPrimaryCapabilityLabel, GMP_CERTIFICATIONS_OPTIONS, OPERATING_REGIONS_OPTIONS, PRIMARY_CAPABILITIES_OPTIONS, PROJECT_TYPES_OPTIONS, SELECTED_TILE_CLASS, SPEED_TO_START_OPTIONS, totalSteps } from '../constants';
import { type CapabilityGroup, canonicalizeDetailedCapabilityLabels, countCapabilityOptions, filterCapabilityGroups, groupCapabilityOptions } from '../constants/detailedCapabilities';
import FacilityCapabilitySelect from './common/FacilityCapabilitySelect';
import { normalizeRegulatoryCertificationLabel } from '../lib/gatekeeperFilters';
import { VALIDATION_MESSAGES } from '../constants/messages';

/** Exact message required under a locked field (AC) */
const LOCKED_FIELD_MESSAGE = VALIDATION_MESSAGES.LOCKED_FIELD_DISCLAIMER;

/**
 * Batch-size column shown in the profile UI — must stay aligned with {@link BATCH_SIZES_COMBINED_OPTIONS} indices.
 */
function getBatchSizeOptionsForPrimaryCapabilities(primaryCapabilities: string[]): readonly string[] {
  const hasSmallMolecule = primaryCapabilities.includes('Small Molecule');
  const hasBiologics = primaryCapabilities.includes('Biologics');
  if (hasSmallMolecule && hasBiologics) return BATCH_SIZES_COMBINED_OPTIONS;
  if (hasBiologics) return BATCH_SIZES_L_OPTIONS;
  return BATCH_SIZES_KG_OPTIONS;
}

/** Tier index 0..4 for any canonical kg / L / combined batch label; -1 if unknown. */
function tierIndexForBatchSizeLabel(label: string): number {
  const i = BATCH_SIZES_COMBINED_OPTIONS.indexOf(label);
  if (i !== -1) return i;
  const j = BATCH_SIZES_KG_OPTIONS.indexOf(label);
  if (j !== -1) return j;
  const k = BATCH_SIZES_L_OPTIONS.indexOf(label);
  if (k !== -1) return k;
  return -1;
}

/**
 * When primary capabilities change (or on load), keep only tiers that still apply and rewrite labels
 * to the active column so stale combined/L/kg strings are not left in state/DB while the UI shows fewer checks.
 */
function remapTypicalBatchSizesForCapabilities(
  previous: string[] | undefined,
  primaryCapabilities: string[]
): string[] {
  if (primaryCapabilities.length === 0) return [];
  const column = getBatchSizeOptionsForPrimaryCapabilities(primaryCapabilities);
  const indices = new Set<number>();
  for (const sel of previous || []) {
    const idx = tierIndexForBatchSizeLabel(sel);
    if (idx === -1) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[CDMOProfile] Unmapped typical_batch_sizes label; entry skipped during capability remap:',
          sel
        );
      }
      continue;
    }
    indices.add(idx);
  }
  return [...indices]
    .sort((a, b) => a - b)
    .map((i) => column[i])
    .filter(Boolean);
}

/**
 * Rewrites pre-rename detailed-capability labels to their current names, per modality.
 *
 * `detailed_capabilities` values are compared to the canonical taxonomy by exact string
 * (`capabilityGroupsByModality` below, and the Tier 1 gate in `gatekeeperFilters.ts`). Some
 * capability labels were renamed by a taxonomy expansion; a migration rewrites stored rows, but a
 * profile loaded before it runs still carries the old label — without this, it renders as an
 * unlabelled extra tile (via the canonical-plus-stored fallback in `capabilityGroupsByModality`)
 * sitting checked next to its own renamed, unchecked canonical tile. Resolution goes through the
 * shared {@link canonicalizeDetailedCapabilityLabels}, so this stays in agreement with
 * `gatekeeperFilters.ts` and `sponsorCardDetails.ts`. Same role as
 * `normalizeRegulatoryCertificationLabel` for `gmp_certifications` below.
 *
 * A modality value that is not an array (the alias table's docblock notes rows can carry a stray
 * non-array key, e.g. `{"Biologics": [...], "notes": "legacy"}`) passes through unchanged rather
 * than being coerced to `[]`, since this result is persisted verbatim on save.
 */
function normalizeStoredDetailedCapabilities(
  stored: Record<string, string[]> | undefined | null
): Record<string, string[]> {
  if (!stored) return {};
  const result: Record<string, string[]> = {};
  for (const [modality, options] of Object.entries(stored)) {
    result[modality] = Array.isArray(options) ? canonicalizeDetailedCapabilityLabels(options) : options;
  }
  return result;
}

/**
 * Applies the same legacy-label rewrite as {@link normalizeStoredDetailedCapabilities} to each
 * facility's `capabilities`, so facility-level entries stay in the same vocabulary as
 * `detailedCapabilities` — otherwise a pre-migration facility capability no longer string-matches
 * its profile-level counterpart, misrenders as "no longer in your profile", and is dropped from
 * form state the next time any capability is toggled (by {@link pruneFacilityCapabilities}), with
 * the loss persisted on the following save.
 */
function normalizeFacilityCapabilities(facilities: CDMOFacility[] | undefined | null): CDMOFacility[] {
  return (facilities ?? []).map(facility => ({
    ...facility,
    capabilities: canonicalizeDetailedCapabilityLabels(facility.capabilities),
  }));
}

// Sanitize input to prevent XSS attacks
const sanitizeInput = (input?: string) => {
  return input ? Dompurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) : '';
};

/** Free-text limits for a facility record, enforced in the inputs and again on save. */
const FACILITY_LOCATION_MAX_LENGTH = 200;
const FACILITY_EQUIPMENT_MAX_LENGTH = 2000;

/**
 * Facility records under a region the CDMO still has selected.
 *
 * Unticking a region leaves its records in form state so the choice can be undone, but they have no
 * section to render in and no way to be edited or removed. Both the review summary and the save
 * payload go through here, so nothing unreachable is counted or persisted — a saved orphan would
 * report facilities in a region the CDMO no longer operates in.
 */
function facilitiesInSelectedRegions(
  facilities: CDMOFacility[] | undefined,
  operatingRegions: string[] | undefined
): CDMOFacility[] {
  const selected = new Set(operatingRegions ?? []);
  return (facilities ?? []).filter(facility => selected.has(facility.region));
}

/**
 * Drops facility-level capability assignments the company no longer claims.
 *
 * `FacilityCapabilitySelect` offers only the profile's detailed capabilities, so a capability
 * withdrawn at profile level leaves an assignment with no row left to untick, and saving it would
 * break that component's stated invariant — a facility cannot list a capability the company has not
 * claimed. Returns the original array when nothing changed, so form state keeps its identity and
 * dependent effects do not re-run.
 */
function pruneFacilityCapabilities(
  facilities: CDMOFacility[] | undefined,
  detailedCapabilities: Record<string, string[]> | undefined
): CDMOFacility[] {
  const existing = facilities ?? [];
  const claimed = new Set(Object.values(detailedCapabilities ?? {}).flat());
  let changed = false;
  const next = existing.map(facility => {
    const kept = (facility.capabilities ?? []).filter(capability => claimed.has(capability));
    if (kept.length === (facility.capabilities ?? []).length) return facility;
    changed = true;
    return { ...facility, capabilities: kept };
  });
  return changed ? next : existing;
}

interface CDMOProfileProps {
  mode: 'create' | 'edit';
  cdmoId?: string;
}

const CDMOProfile: React.FC<CDMOProfileProps> = ({ mode, cdmoId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    status: CDMO_STATUS.CLAIMED, // Default to claimed for normal users
    cdmoBusinessProfile: 'Mid-Size Specialist',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    feiNumbers: [''], // Optional FEI numbers for FDA lookups (up to 10)
    primaryContactEmail: '',
    additionalContactEmails: ['', '', '', ''], // 4 additional slots
    adminNotes: '',
    primaryCapabilities: [],
    detailedCapabilities: {},
    facilities: [],
    typicalBatchSizes: [],
    targetClientTypes: [],
    engagementScales: [],
    preferredProjectTypes: [],
    gmpCertifications: [],
    facilityLocationsDetails: '',
    speedToStart: '',
    willingnessFeasibilityStudies: false,
    budgetRanges: [],
    operatingRegions: [],
    startupFriendly: false,
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  /** Per-modality "Search capabilities" text on the Primary Capabilities step. Display-only — never saved. */
  const [capabilitySearch, setCapabilitySearch] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [adminCreatedCdmoId, setAdminCreatedCdmoId] = useState<string | null>(null);
  const [isAdminCreatedProfile, setIsAdminCreatedProfile] = useState(false);

  /** Admin-only: field keys that are verified/locked; CDMO cannot edit these. Default OFF. */
  const [lockedFields, setLockedFields] = useState<string[]>([]);

  // Admin Force Transfer state
  const originalEmailRef = useRef<string>('');
  const [showForceTransferModal, setShowForceTransferModal] = useState(false);
  const [forceTransferNewEmail, setForceTransferNewEmail] = useState('');
  const [isForceTransferring, setIsForceTransferring] = useState(false);

  // Admin Force Unclaim state
  const originalStatusRef = useRef<string>('');
  const [showForceUnclaimModal, setShowForceUnclaimModal] = useState(false);
  const [isForceUnclaiming, setIsForceUnclaiming] = useState(false);

  /** The website and contact emails saved on the loaded profile (lower case), for checkEmailDomain. */
  const savedContactsRef = useRef<{ website: string; emails: Set<string> }>({ website: '', emails: new Set() });

  /**
   * Whether an email is on the website's domain. An email already saved on this profile passes while
   * the website is unchanged: the bulk upload may save a subsidiary's address (PHAR-485, with a
   * warning), and that profile must stay savable. A new or changed email, or a new website, is checked.
   */
  const checkEmailDomain = (email: string, website: string) => {
    const saved = savedContactsRef.current;
    const norm = (value: string | undefined) => (value ?? '').trim().toLowerCase();
    if (norm(email) && norm(website) === saved.website && saved.emails.has(norm(email))) return { isValid: true };
    return validateEmailDomainMatch(email, website);
  };

  // Delete FEI number confirmation
  const [deleteFeiIndex, setDeleteFeiIndex] = useState<number | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  /** When true, next paint should scroll to the first validation error (e.g. after Next click). */
  const shouldScrollToErrorRef = useRef(false);

  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
    onClose?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  const showMessageModal = (title: string, message: string, type: 'error' | 'success' | 'warning' = 'error', onClose?: () => void) => {
    setMessageModal({
      isOpen: true,
      title,
      message,
      type,
      onClose
    });
  };

  /** Whether this field is verified/locked (only CDMO cannot edit). */
  const isFieldLocked = (fieldKey: string) => lockedFields.includes(fieldKey);
  /** Admin-only: toggle lock for a field. Default OFF; when ON, field is locked for CDMO. */
  const toggleFieldLock = (fieldKey: string) => {
    if (!isAdmin) return;
    setLockedFields(prev => prev.includes(fieldKey) ? prev.filter(k => k !== fieldKey) : [...prev, fieldKey]);
  };

  /**
   * Grouped capability tiles and the option total behind "N of M selected", per selected modality.
   *
   * Offers the canonical taxonomy **plus** anything this profile already stores, so a stored label
   * the rename migration missed still gets a tile — in the trailing unlabelled group — and still
   * counts towards the total. Building the list from the taxonomy alone let the selected count exceed
   * the total ("14 of 25 selected" with 13 tiles lit) and left the legacy value invisible and
   * unremovable, so ticking its renamed tile appended the new label alongside the old one.
   *
   * Memoized because the render path reads this for every selected modality on every keystroke in
   * any of the per-modality search boxes. A Map rather than a plain object: modality keys come from
   * stored profiles, and a bare index would resolve `Object.prototype` members.
   */
  const capabilityGroupsByModality = useMemo(() => {
    const byModality = new Map<string, { groups: CapabilityGroup[]; totalCount: number }>();
    for (const modality of formData?.primaryCapabilities ?? []) {
      const canonical = getDetailedCapabilityOptions(modality);
      const stored = formData?.detailedCapabilities?.[modality] ?? [];
      const options = [...canonical, ...stored.filter(option => !canonical.includes(option))];
      const groups = groupCapabilityOptions(modality, options);
      byModality.set(modality, { groups, totalCount: countCapabilityOptions(groups) });
    }
    return byModality;
  }, [formData?.primaryCapabilities, formData?.detailedCapabilities]);


  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);

        let currentAdminStatus = false;
        // Check admin status - needed for both create and edit modes
        if (user?.email) {
          try {
            currentAdminStatus = await dbService.isUserAdmin(user.email);
            setIsAdmin(currentAdminStatus);
            if (currentAdminStatus && mode === 'create') {
              setFormData(prev => ({ ...prev, status: CDMO_STATUS.UNCLAIMED }));
            } else if (!currentAdminStatus && mode === 'create') {
              // Auto-populate email for CDMO signup and fetch it from users table/context
              setFormData(prev => ({ ...prev, email: user.email || '' }));
            }
          } catch (adminError) {
            console.warn('Admin check failed:', adminError);
          }
        }

        let profileData = null;

        // 1. Try to fetch existing profile linked to the user
        if (user?.id) {
          if (cdmoId && currentAdminStatus) {
            // Admin is editing a specific profile
            profileData = await dbService.getCDMOProfileById(cdmoId);
          } else {
            // User is editing their own profile or just claimed one
            profileData = await dbService.getCDMOProfile(user.id);
          }
        } else if (cdmoId) {
          // Public view or specific ID fetch without session
          profileData = await dbService.getCDMOProfileById(cdmoId);
        }

        // 2. If no profile found and in create mode, check for admin-created data by email
        if (!profileData && mode === 'create' && user?.email) {
          profileData = await dbService.getAdminCreatedData(user.email);
          if (profileData && profileData.id) {
            // Store the ID so we update this record instead of creating a new one
            setAdminCreatedCdmoId(profileData.id);
          }
        }

        if (profileData) {
          // Store the ID so we update this record instead of creating a new one
          if (profileData.id) {
            setAdminCreatedCdmoId(profileData.id);
          }
          // Track original email for admin force transfer detection
          const loadedEmail = profileData?.email || (currentAdminStatus ? '' : user?.email || '');
          originalEmailRef.current = loadedEmail.trim().toLowerCase();
          // Track original status for admin force unclaim detection
          originalStatusRef.current = profileData?.status || CDMO_STATUS.CLAIMED;

          if (profileData.is_admin_created && profileData.status === CDMO_STATUS.CLAIMED) {
            setIsAdminCreatedProfile(true);
          }

          setLockedFields(Array.isArray(profileData.locked_fields) ? profileData.locked_fields : []);

          savedContactsRef.current = {
            website: (profileData.website ?? '').trim().toLowerCase(),
            emails: new Set(
              [profileData.email, profileData.primary_email, ...(profileData.secondary_emails ?? [])]
                .map((address) => (address ?? '').trim().toLowerCase())
                .filter(Boolean)
            ),
          };

          setFormData({
            companyName: profileData.company_name || '',
            status: (profileData?.status as CDMO_ENUM) || CDMO_STATUS.CLAIMED,
            cdmoBusinessProfile: profileData.cdmo_business_profile || 'Mid-Size Specialist',
            contactName: profileData.contact_name || '',
            email: loadedEmail,
            phone: profileData.phone || '',
            website: profileData.website || '',
            feiNumbers: profileData.fei_number
              ? profileData.fei_number.split(',').map((f: string) => f.trim()).filter(Boolean)
              : [''],
            primaryContactEmail: profileData.primary_email || '',
            additionalContactEmails: profileData.secondary_emails?.length === 4
              ? profileData.secondary_emails
              : [...(profileData.secondary_emails || []), '', '', '', ''].slice(0, 4),
            adminNotes: profileData.admin_notes || '',
            primaryCapabilities: profileData.primary_capabilities || [],
            detailedCapabilities: normalizeStoredDetailedCapabilities(profileData.detailed_capabilities),
            facilities: normalizeFacilityCapabilities(profileData.facilities),
            typicalBatchSizes: remapTypicalBatchSizesForCapabilities(
              profileData.typical_batch_sizes,
              profileData.primary_capabilities || []
            ),
            targetClientTypes: profileData.target_client_types || [],
            engagementScales: profileData.engagement_scales || [],
            preferredProjectTypes: profileData.preferred_project_types || [],
            gmpCertifications: (profileData.gmp_certifications || [])
              .map((c: string) => normalizeRegulatoryCertificationLabel(c))
              .filter((c: string) => GMP_CERTIFICATIONS_OPTIONS.includes(c)),
            facilityLocationsDetails: profileData.facility_locations_details || '',
            speedToStart: profileData.speed_to_start || '',
            willingnessFeasibilityStudies: profileData.willingness_feasibility_studies || false,
            budgetRanges: profileData.budget_ranges || [],
            operatingRegions: profileData.operating_regions || [],
            startupFriendly: profileData.startup_friendly || false,
          });
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setSubmissionError(VALIDATION_MESSAGES.FAILED_LOAD_PROFILE);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.id || cdmoId) {
      fetchProfileData();
    } else if (mode === 'create') {
      // If no user but in create mode (e.g. initial load), still clear loading
      setIsLoading(false);
    }
  }, [mode, user?.id, user?.email, cdmoId]);

  // Scroll to first validation error when errors are set from Next (or submit) so the field is visible
  useEffect(() => {
    if (!shouldScrollToErrorRef.current || Object.keys(validationErrors).length === 0) return;
    const scrollToErrorOrder: string[] = [
      'companyName', 'contactName', 'email', 'phone', 'website',
      ...Array.from({ length: 10 }, (_, i) => `feiNumber_${i}`),
      'primaryContactEmail',
      'additionalEmail_0', 'additionalEmail_1', 'additionalEmail_2', 'additionalEmail_3',
      'primaryCapabilities', 'typicalBatchSizes', 'budgetRanges', 'operatingRegions', 'facilityLocationsDetails',
    ];
    const firstKey = scrollToErrorOrder.find(k => validationErrors[k]);
    if (!firstKey) return;
    shouldScrollToErrorRef.current = false;
    requestAnimationFrame(() => {
      const el = document.getElementById(firstKey);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [validationErrors]);

  const validateField = (name: string, value: string) => {
    let validation;

    switch (name) {
      case 'companyName':
        validation = validateCompany(value);
        break;
      case 'contactName':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        if (validation.isValid && formData.website) {
          const domainMatch = checkEmailDomain(value, formData.website);
          if (!domainMatch.isValid) validation = domainMatch;
        }
        break;
      case 'phone':
        validation = validatePhone(value);
        break;
      case 'primaryContactEmail':
        validation = validateEmail(value);
        if (validation.isValid && formData.website) {
          const domainMatch = checkEmailDomain(value, formData.website);
          if (!domainMatch.isValid) validation = domainMatch;
        }
        break;
      case 'website':
        validation = validateUrl(value, true);
        if (validation.isValid && value && formData.email) {
          const domainMatch = checkEmailDomain(formData.email, value);
          if (!domainMatch.isValid) validation = domainMatch;
        }
        break;
      case 'facilityLocationsDetails':
        validation = validateTextArea(value, 'Facility locations details', 10, 2000, false);
        break;
      // feiNumbers is handled separately via handleFeiNumberChange
      case 'feiNumber':
        validation = validateFeiNumber(value);
        break;
      default:
        validation = { isValid: true };
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: validation.isValid ? '' : (validation.error || 'Invalid value')
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    // ── Admin Force Unclaim Detection ──
    // If admin is switching an existing CLAIMED profile to UNCLAIMED, intercept and show modal
    if (
      name === 'status' &&
      value === CDMO_STATUS.UNCLAIMED &&
      isAdmin &&
      mode === 'edit' &&
      adminCreatedCdmoId &&
      originalStatusRef.current === CDMO_STATUS.CLAIMED
    ) {
      setShowForceUnclaimModal(true);
      // Do NOT update formData yet — modal must be confirmed first
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Real-time validation for relevant fields
    if (['companyName', 'contactName', 'email', 'phone', 'website', 'facilityLocationsDetails', 'primaryContactEmail'].includes(name)) {
      validateField(name, value);
    }

    // When primaryContactEmail or account email changes, re-validate all emails for duplicates
    if (name === 'primaryContactEmail' || name === 'email') {
      setFormData(prev => {
        const newPrimaryNorm = (name === 'primaryContactEmail' ? value : prev?.primaryContactEmail || '').trim().toLowerCase();
        const newAccountNorm = (name === 'email' ? value : prev?.email || '').trim().toLowerCase();
        const additionalEmails = prev?.additionalContactEmails || [];
        const newErrors: Record<string, string> = {};

        // 1. Check Primary vs Account Email
        if (newPrimaryNorm && newAccountNorm && newPrimaryNorm === newAccountNorm) {
          newErrors.primaryContactEmail = VALIDATION_MESSAGES.PRIMARY_DIFFERENT_FROM_EMAIL;
          newErrors.email = VALIDATION_MESSAGES.EMAIL_DIFFERENT_FROM_PRIMARY;
        } else {
          // Clear "different from" errors if they existed
          if (validationErrors.primaryContactEmail === VALIDATION_MESSAGES.PRIMARY_DIFFERENT_FROM_EMAIL) {
            newErrors.primaryContactEmail = '';
          }
          if (validationErrors.email === VALIDATION_MESSAGES.EMAIL_DIFFERENT_FROM_PRIMARY) {
            newErrors.email = '';
          }
        }

        // 2. Check Additional Emails vs Primary and Account Email
        additionalEmails.forEach((additionalEmail, index) => {
          const trimmed = additionalEmail.trim().toLowerCase();
          if (trimmed) {
            if (newPrimaryNorm && trimmed === newPrimaryNorm) {
              newErrors[`additionalEmail_${index}`] =
                VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_PRIMARY(index);
            } else if (newAccountNorm && trimmed === newAccountNorm) {
              newErrors[`additionalEmail_${index}`] =
                VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_ACCOUNT(index);
            } else {
              // Clear only if it was one of these specific uniqueness errors
              const currentError = validationErrors[`additionalEmail_${index}`];
              if (
                currentError === VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_PRIMARY(index) ||
                currentError === VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_ACCOUNT(index)
              ) {
                newErrors[`additionalEmail_${index}`] = '';
              }
            }
          }
        });

        setValidationErrors(prevErrors => ({ ...prevErrors, ...newErrors }));
        return prev; // formData already updated above
      });
    }

    // Clear contact errors if switching to unclaimed
    if (name === 'status' && value === CDMO_STATUS.UNCLAIMED) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.contactName;
        delete newErrors.email;
        delete newErrors.phone;
        return newErrors;
      });
    }
  };

  const handleMultiSelectToggle = (field: keyof FormData, value: string) => {
    const requiredMultiSelectFields = ['typicalBatchSizes', 'budgetRanges', 'primaryCapabilities', 'operatingRegions'];
    setFormData(prev => {
      const currentArray = (prev?.[field] as string[] | undefined) ?? [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      if (requiredMultiSelectFields.includes(field as string) && newArray.length > 0) {
        setValidationErrors(prevErr => ({ ...prevErr, [field]: '' }));
      }
      return { ...prev, [field]: newArray };
    });
  };

  /** Regions this session has already seeded a facility for. See the seeding effect below. */
  const seededRegionsRef = useRef<Set<string>>(new Set());

  /**
   * Every newly selected region gets one facility record to fill in.
   *
   * §5: selecting a region *creates* a Facility Details section, so the CDMO should find a facility
   * ready rather than an empty region with only an "Add another facility" button. Also covers
   * loading an existing profile that has regions but no saved facilities.
   *
   * Seeds a region only the first time it is seen, tracked in `seededRegionsRef`. Deriving the work
   * list from `facilities` alone made Remove inoperable: deleting the last facility in a region put
   * that region straight back into `missing`, and this effect recreated it — so the button appeared
   * broken and silently discarded whatever the CDMO had typed into that record.
   *
   * Records for unselected regions are deliberately left in state rather than pruned, so unticking
   * a region by accident does not destroy anything the CDMO typed — re-ticking restores it. They are
   * dropped on save by {@link facilitiesInSelectedRegions}, so nothing unreachable is persisted.
   */
  useEffect(() => {
    // A locked field must not be seeded: the inputs render disabled, but appended records would
    // still be written on the next save, since locked_fields has no server-side enforcement.
    // Reads `lockedFields` directly rather than through isFieldLocked, which is recreated per render
    // and so cannot be an effect dependency.
    if (lockedFields.includes('facilities') && !isAdmin) return;

    const regions = formData?.operatingRegions ?? [];
    if (regions.length === 0) return;

    const existing = formData?.facilities ?? [];
    const missing = regions.filter(
      region =>
        !seededRegionsRef.current.has(region) &&
        !existing.some(facility => facility.region === region)
    );
    if (missing.length === 0) return;

    missing.forEach(region => seededRegionsRef.current.add(region));
    setFormData(prev => ({
      ...prev,
      facilities: [
        ...(prev?.facilities ?? []),
        ...missing.map(region => ({
          id: `facility-${region}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          region,
          location: '',
          capabilities: [],
          equipment: '',
        })),
      ],
    }));
  }, [formData?.operatingRegions, formData?.facilities, lockedFields, isAdmin]);

  /**
   * Whether this user may edit facility records.
   *
   * The buttons and inputs are already withheld when the field is locked; this makes the lock
   * authoritative in the handlers too, matching {@link performDeleteFeiAtIndex}.
   */
  const canEditFacilities = () => !(isFieldLocked('facilities') && !isAdmin);

  /**
   * Appends an empty facility record under a region.
   *
   * Every field starts blank because all of them are optional — a CDMO may record equipment without
   * naming a location, or a location without equipment.
   */
  const handleAddFacility = (region: string) => {
    if (!canEditFacilities()) return;
    setFormData(prev => ({
      ...prev,
      facilities: [
        ...(prev?.facilities ?? []),
        {
          id: `facility-${region}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          region,
          location: '',
          capabilities: [],
          equipment: '',
        },
      ],
    }));
  };

  const handleRemoveFacility = (facilityId: string) => {
    if (!canEditFacilities()) return;
    setFormData(prev => ({
      ...prev,
      facilities: (prev?.facilities ?? []).filter(facility => facility.id !== facilityId),
    }));
  };

  const handleFacilityChange = <K extends keyof CDMOFacility>(
    facilityId: string,
    field: K,
    fieldValue: CDMOFacility[K]
  ) => {
    if (!canEditFacilities()) return;
    setFormData(prev => ({
      ...prev,
      facilities: (prev?.facilities ?? []).map(facility =>
        facility.id === facilityId ? { ...facility, [field]: fieldValue } : facility
      ),
    }));
  };

  const handlePrimaryCapabilityToggle = (capability: string) => {
    setFormData(prev => {
      const newPrimaryCapabilities = prev?.primaryCapabilities?.includes(capability)
        ? prev?.primaryCapabilities?.filter(c => c !== capability)
        : [...(prev?.primaryCapabilities || []), capability];

      // Clean up detailed capabilities if primary capability is removed
      const newDetailedCapabilities = { ...prev?.detailedCapabilities };
      if (!newPrimaryCapabilities?.includes(capability)) {
        delete newDetailedCapabilities[capability];
      }

      return {
        ...prev,
        primaryCapabilities: newPrimaryCapabilities,
        detailedCapabilities: newDetailedCapabilities,
        // Dropping a whole modality drops its capabilities, so any facility that had one assigned
        // would otherwise keep listing a capability the company no longer claims.
        facilities: pruneFacilityCapabilities(prev?.facilities, newDetailedCapabilities),
        typicalBatchSizes: remapTypicalBatchSizesForCapabilities(
          prev.typicalBatchSizes,
          newPrimaryCapabilities
        ),
      };
    });
    // Clear required-field error when user adds a capability (so the new list will have at least one)
    if (!formData?.primaryCapabilities?.includes(capability)) {
      setValidationErrors(prev => ({ ...prev, primaryCapabilities: '' }));
    }
  };

  const handleDetailedCapabilityToggle = (primaryCapability: string, detailedCapability: string) => {
    setFormData(prev => {
      const currentDetailed = prev?.detailedCapabilities?.[primaryCapability] || [];
      const newDetailed = currentDetailed?.includes(detailedCapability)
        ? currentDetailed?.filter(c => c !== detailedCapability)
        : [...(currentDetailed || []), detailedCapability];

      const newDetailedCapabilities = {
        ...prev.detailedCapabilities,
        [primaryCapability]: newDetailed
      };

      return {
        ...prev,
        detailedCapabilities: newDetailedCapabilities,
        // Unticking a capability here also withdraws it from every facility it was assigned to; the
        // facility picker offers only what the profile claims, so it would have no row left to untick.
        facilities: pruneFacilityCapabilities(prev?.facilities, newDetailedCapabilities),
      };
    });
  };

  // ─── Delete FEI number: perform removal at index (used for both direct delete and after confirm) ───
  const performDeleteFeiAtIndex = (index: number) => {
    if (index < 0) return;
    /** Admin-verified FEI list cannot be deleted by CDMO (including after claim). */
    if (isFieldLocked('feiNumbers') && !isAdmin) {
      setDeleteFeiIndex(null);
      return;
    }

    let newFeiNumbersForEffect: string[] = [];

    setFormData(prev => {
      const feiNumbers = prev?.feiNumbers ?? [''];
      const updatedFeis = feiNumbers.filter((_, i) => i !== index);
      const newFeiNumbers = updatedFeis.length > 0 ? updatedFeis : [''];
      newFeiNumbersForEffect = newFeiNumbers;
      return { ...prev, feiNumbers: newFeiNumbers };
    });
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[`feiNumber_${index}`];
      return next;
    });

    const targetCdmoId = cdmoId || adminCreatedCdmoId;
    if (targetCdmoId) {
      queueMicrotask(() => {
        void (async () => {
          const feiString = newFeiNumbersForEffect
            .map((f: string) => sanitizeInput(f.trim()))
            .filter(Boolean)
            .join(',') || null;

          const { error } = await supabase
            .from('cdmo_applications')
            .update({ fei_number: feiString })
            .eq('id', targetCdmoId);

          if (error) {
            showMessageModal(VALIDATION_MESSAGES?.MODAL_TITLES?.ERROR ?? 'Error', VALIDATION_MESSAGES?.FEI_DELETE_FAILED ?? 'Failed to update FEI numbers.', 'error');
          }
        })().catch((err: unknown) => {
          console.error('Catch Error in FEI delete DB update:', err);
          if (err instanceof Error) {
            showMessageModal(VALIDATION_MESSAGES?.MODAL_TITLES?.ERROR ?? 'Error', err.message, 'error');
          }
        });
      });
    }

    setDeleteFeiIndex(null);
  };

  // ─── Delete FEI number: handler for confirming delete (when field has value) ───
  const handleDeleteFeiConfirm = () => {
    const index = deleteFeiIndex ?? -1;
    if (index < 0) return;
    if (isFieldLocked('feiNumbers') && !isAdmin) {
      setDeleteFeiIndex(null);
      return;
    }
    performDeleteFeiAtIndex(index);
  };

  // ─── Admin Force Unclaim: handler for confirming unclaim ───
  const handleForceUnclaimConfirm = async () => {
    if (!adminCreatedCdmoId || !user?.email) {
      showMessageModal('Unclaim Error', VALIDATION_MESSAGES.ADMIN_FORCE_UNCLAIM_NO_ID, 'error');
      return;
    }

    setIsForceUnclaiming(true);
    try {
      const result = await dbService.adminForceUnclaim(
        adminCreatedCdmoId,
        user.email,
      );

      if (result.success) {


        // Update formData to reflect the unclaimed state
        setFormData(prev => ({
          ...prev,
          status: CDMO_STATUS.UNCLAIMED,
          email: '',
          contactName: '',
        }));

        // Update originalStatusRef so subsequent saves don't re-trigger the modal
        originalStatusRef.current = CDMO_STATUS.UNCLAIMED;
        originalEmailRef.current = '';

        // Clear validation errors on cleared fields
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.contactName;
          delete newErrors.email;
          delete newErrors.phone;
          return newErrors;
        });

        setShowForceUnclaimModal(false);
        showMessageModal(
          VALIDATION_MESSAGES.MODAL_TITLES.SUCCESS,
          VALIDATION_MESSAGES.ADMIN_FORCE_UNCLAIM_SUCCESS,
          'success'
        );
      }
    } catch (error: any) {
      console.error('Force unclaim failed:', error);
      setShowForceUnclaimModal(false);
      showMessageModal(
        VALIDATION_MESSAGES.MODAL_TITLES.UNCLAIM_FAILED,
        error?.message || VALIDATION_MESSAGES.ADMIN_FORCE_UNCLAIM_FAILED,
        'error'
      );
    } finally {
      setIsForceUnclaiming(false);
    }
  };

  // ─── Admin Force Transfer: handler for confirming transfer ───
  const handleForceTransferConfirm = async () => {
    if (!adminCreatedCdmoId || !forceTransferNewEmail || !user?.email) return;

    setIsForceTransferring(true);
    try {
      const result = await dbService.adminForceTransfer(
        adminCreatedCdmoId,
        forceTransferNewEmail,
        user.email,
      );

      if (result.success) {
        setShowForceTransferModal(false);
        showMessageModal(
          VALIDATION_MESSAGES.MODAL_TITLES.SUCCESS,
          VALIDATION_MESSAGES.ADMIN_FORCE_TRANSFER_SUCCESS,
          'success'
        );
        // Update originalEmailRef so the form doesn't re-trigger on subsequent saves
        originalEmailRef.current = forceTransferNewEmail.trim().toLowerCase();
        // Continue to next step
        if (currentStep < totalSteps) {
          setCurrentStep(currentStep + 1);
        }
      }
    } catch (error: any) {
      console.error('Force transfer failed:', error);
      setShowForceTransferModal(false);
      showMessageModal(
        VALIDATION_MESSAGES.MODAL_TITLES.TRANSFER_FAILED,
        error?.message || VALIDATION_MESSAGES.ADMIN_FORCE_TRANSFER_FAILED,
        'error'
      );
      // Revert the email back to original
      setFormData(prev => ({ ...prev, email: originalEmailRef.current }));
    } finally {
      setIsForceTransferring(false);
      setShowForceTransferModal(false);
    }
  };

  const nextStep = async () => {
    const errors: Record<string, string> = {};
    setIsValidating(true);

    try {
      if (currentStep === 1) {
        const isClaimed = formData?.status === CDMO_STATUS.CLAIMED;
        const isUnclaimed = formData?.status === CDMO_STATUS.UNCLAIMED;

        // Company Name is always required
        if (!formData?.companyName?.trim()) errors.companyName = VALIDATION_MESSAGES.COMPANY_NAME_REQUIRED || 'Company name is required';

        // Website is always required
        const urlValid = validateUrl(formData?.website, true);
        if (!urlValid.isValid) errors.website = urlValid.error || 'Valid website is required';

        if (isClaimed) {
          if (!formData?.contactName?.trim()) errors.contactName = VALIDATION_MESSAGES.NAME_REQUIRED || 'Contact name is required';
          const emailValid = validateEmail(formData?.email);
          if (!emailValid.isValid) errors.email = emailValid.error || 'Valid email is required';
          const phoneValid = validatePhone(formData?.phone);
          if (!phoneValid.isValid) errors.phone = phoneValid.error || 'Valid phone number is required';
        }

        if (isUnclaimed && isAdmin) {
          const primaryEmailValid = validateEmail(formData?.primaryContactEmail);
          if (!primaryEmailValid.isValid) errors.primaryContactEmail = primaryEmailValid.error || 'Valid primary contact email is required';
        }
        // Domain matching for the main account email
        if (formData?.email && formData?.website) {
          const domainMatch = checkEmailDomain(formData?.email, formData?.website);
          if (!domainMatch.isValid) {
            errors.email = domainMatch.error || 'Email domain must match website domain';
          }
        }

        // Domain matching + uniqueness for Admin-managed contact emails
        if (isAdmin && formData?.website) {
          // Validate Primary Contact Email domain
          if (formData?.primaryContactEmail) {
            const primaryMatch = checkEmailDomain(formData.primaryContactEmail, formData.website);
            if (!primaryMatch.isValid) {
              errors.primaryContactEmail = primaryMatch.error || 'Primary email domain must match website domain';
            }
          }

          // Validate Additional Contact Emails domain
          formData?.additionalContactEmails?.forEach((email, index) => {
            if (email?.trim()) {
              const secondaryMatch = checkEmailDomain(email, formData.website);
              if (!secondaryMatch.isValid) {
                errors[`additionalEmail_${index}`] = secondaryMatch.error || VALIDATION_MESSAGES.DOMAIN_MISMATCH_ERROR;
              }
            }
          });
        }

        // Uniqueness check: all admin contact emails must be distinct
        if (isAdmin) {
          const primaryNorm = formData?.primaryContactEmail?.trim().toLowerCase();
          const accountEmailNorm = formData?.email?.trim().toLowerCase();
          const filledAdditional = (formData?.additionalContactEmails || [])
            .map((e, i) => ({ email: e?.trim().toLowerCase(), index: i }))
            .filter(({ email }) => !!email);

          // Check primary vs account
          if (primaryNorm && accountEmailNorm && primaryNorm === accountEmailNorm) {
            errors.primaryContactEmail = VALIDATION_MESSAGES.PRIMARY_DIFFERENT_FROM_EMAIL;
            errors.email = VALIDATION_MESSAGES.EMAIL_DIFFERENT_FROM_PRIMARY;
          }

          // Check each additional email against primary and account
          filledAdditional.forEach(({ email, index }) => {
            if (primaryNorm && email === primaryNorm) {
              errors[`additionalEmail_${index}`] =
                VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_PRIMARY(index);
            } else if (accountEmailNorm && email === accountEmailNorm) {
              errors[`additionalEmail_${index}`] =
                VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_ACCOUNT(index);
            }
          });

          // Check additional emails against each other
          filledAdditional.forEach(({ email, index }) => {
            filledAdditional.forEach(({ email: otherEmail, index: otherIndex }) => {
              if (index !== otherIndex && email === otherEmail && !errors[`additionalEmail_${index}`]) {
                errors[`additionalEmail_${index}`] =
                  VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DUPLICATE(index);
              }
            });
          });
        }

        // FEI Numbers format validation and database check (each individual entry)
        const seenFeis = new Set<string>();
        const feiEntries = formData?.feiNumbers || [];
        
        // Parallelized check for better performance
        const feiChecks = feiEntries.map(async (feiValue, i) => {
          const fei = feiValue?.trim();
          if (fei) {
            // 1. Format validation
            const feiValid = validateFeiNumber(fei);
            if (!feiValid.isValid) {
              return { index: i, error: feiValid.error || VALIDATION_MESSAGES.FEI_NUMBER_INVALID };
            }
            
            // 2. Internal duplicate check
            if (seenFeis.has(fei)) {
              return { index: i, error: VALIDATION_MESSAGES.FEI_NUMBER_DUPLICATE_IN_FORM };
            }
            seenFeis.add(fei);

            // 3. Database existence check
            const exists = await dbService.checkFeiNumberExists(fei, cdmoId || adminCreatedCdmoId || undefined);
            if (exists) {
              return { index: i, error: VALIDATION_MESSAGES.FEI_NUMBER_ALREADY_REGISTERED };
            }
          }
          return null;
        });

        const results = await Promise.all(feiChecks);
        results.forEach(res => {
          if (res) {
            errors[`feiNumber_${res.index}`] = res.error;
          }
        });

      } else if (currentStep === 2) {
        if (!formData?.primaryCapabilities || formData.primaryCapabilities.length === 0) {
          errors.primaryCapabilities = VALIDATION_MESSAGES.PRIMARY_MODALITY_REQUIRED;
        }
      } else if (currentStep === 4) {
        if (!formData?.budgetRanges || formData.budgetRanges.length === 0) {
          errors.budgetRanges = VALIDATION_MESSAGES.BUDGET_RANGE_REQUIRED;
        }
        if (!formData?.typicalBatchSizes || formData.typicalBatchSizes.length === 0) {
          errors.typicalBatchSizes = VALIDATION_MESSAGES.BATCH_SIZE_REQUIRED;
        }
      } else if (currentStep === 5) {
        if (!formData?.operatingRegions || formData.operatingRegions.length === 0) {
          errors.operatingRegions = VALIDATION_MESSAGES.OPERATING_REGION_REQUIRED;
        }
      }

      if (Object.keys(errors).length > 0) {
        shouldScrollToErrorRef.current = true;
        setValidationErrors(errors);
        setSubmissionError(null);
        return;
      }

      setValidationErrors({});
      setSubmissionError(null);

      // ── Admin Force Transfer Detection (only on step 1) ──
      if (currentStep === 1) {
        if (
          isAdmin &&
          mode === 'edit' &&
          adminCreatedCdmoId &&
          formData?.status === CDMO_STATUS.CLAIMED &&
          originalEmailRef.current &&
          formData?.email?.trim().toLowerCase() !== originalEmailRef.current
        ) {
          setForceTransferNewEmail(formData.email.trim().toLowerCase());
          setShowForceTransferModal(true);
          return;
        }
      }

      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } catch (err) {
      console.error('Validation error in nextStep:', err);
      setSubmissionError(VALIDATION_MESSAGES.VALIDATION_UNEXPECTED_ERROR);
    } finally {
      setIsValidating(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAdditionalEmailChange = (index: number, value: string) => {
    setFormData(prev => {
      const newEmails = [...(prev?.additionalContactEmails || [])];
      newEmails[index] = value;

      // Real-time validation for this field
      const trimmedValue = value.trim().toLowerCase();
      let error = '';

      if (trimmedValue) {
        // 1. Email format check
        const formatCheck = validateEmail(value);
        if (!formatCheck.isValid) {
          error = formatCheck.error || 'Invalid email address';
        } else {
          // 2. Duplicate vs primary contact email
          const primaryNorm = (prev?.primaryContactEmail || '').trim().toLowerCase();
          if (primaryNorm && trimmedValue === primaryNorm) {
            error = VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_PRIMARY(index);
          }

          // 3. Duplicate vs account email
          const accountEmailNorm = (prev?.email || '').trim().toLowerCase();
          if (!error && accountEmailNorm && trimmedValue === accountEmailNorm) {
            error = VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_ACCOUNT(index);
          }

          // 4. Duplicate vs other additional emails
          if (!error) {
            newEmails.forEach((otherEmail, otherIndex) => {
              if (
                otherIndex !== index &&
                otherEmail.trim().toLowerCase() === trimmedValue
              ) {
                error = VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DUPLICATE(index);
              }
            });
          }
        }
      }

      // Update validationErrors for this specific index
      setValidationErrors(prevErrors => ({
        ...prevErrors,
        [`additionalEmail_${index}`]: error,
      }));

      return { ...prev, additionalContactEmails: newEmails };
    });
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      setSubmissionError(VALIDATION_MESSAGES.LOGIN_REQUIRED_ACTION(mode === 'edit' ? 'update' : 'submit'));
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);
    setValidationErrors({});

    try {
      // Comprehensive validation before submission
      const validationErrors: string[] = [];

      const companyValidation = validateCompany(formData?.companyName);
      if (!companyValidation?.isValid) validationErrors.push(companyValidation?.error!);

      if (formData?.status === CDMO_STATUS.CLAIMED) {
        const nameValidation = validateName(formData?.contactName);
        if (!nameValidation?.isValid) validationErrors.push(nameValidation?.error!);

        const emailValidation = validateEmail(formData?.email);
        if (!emailValidation?.isValid) validationErrors.push(emailValidation?.error!);

        const phoneValidation = validatePhone(formData?.phone);
        if (!phoneValidation?.isValid) validationErrors.push(phoneValidation?.error!);
      }

      const urlValidation = validateUrl(formData?.website, true);
      if (!urlValidation.isValid) {
        validationErrors.push(urlValidation.error!);
      } else {
        // Validation for Primary Contact Email (Mandatory if status = CDMO_STATUS.UNCLAIMED)
        if (formData?.status === CDMO_STATUS.UNCLAIMED) {
          const primaryEmailValidation = validateEmail(formData.primaryContactEmail);
          if (!primaryEmailValidation.isValid) {
            validationErrors.push(`Primary Contact Email: ${primaryEmailValidation.error}`);
          }
        }

        // Validate account email domain matches website domain
        if (formData?.email && formData?.website) {
          const domainMatch = checkEmailDomain(formData?.email, formData?.website);
          if (!domainMatch?.isValid) {
            validationErrors.push(domainMatch?.error!);
          }
        }

        // For Unclaimed profiles (admin created/edited): Validate 'primaryContactEmail'
        if (isAdmin && formData?.primaryContactEmail) {
          const primaryMatch = checkEmailDomain(formData.primaryContactEmail, formData?.website);
          if (!primaryMatch?.isValid) {
            validationErrors.push(`Primary Email: ${primaryMatch?.error}`);
          }
        }

        // Validate additional emails domain match website domain
        formData?.additionalContactEmails?.forEach((email: string, index: number) => {
          if (email?.trim()) {
            const secondaryMatch = checkEmailDomain(email, formData?.website);
            if (!secondaryMatch?.isValid) {
              validationErrors.push(VALIDATION_MESSAGES.ADDITIONAL_EMAIL_INVALID(index, secondaryMatch?.error!));
            }
          }
        });
      }

      // Uniqueness check: all admin contact emails must be distinct
      if (isAdmin) {
        const primaryNorm = formData?.primaryContactEmail?.trim().toLowerCase();
        const accountEmailNorm = formData?.email?.trim().toLowerCase();
        const filledAdditional = (formData?.additionalContactEmails || [])
          .map((e: string, i: number) => ({ email: e?.trim().toLowerCase(), index: i }))
          .filter(({ email }: { email: string }) => !!email);

        // Check primary vs account
        if (primaryNorm && accountEmailNorm && primaryNorm === accountEmailNorm) {
          validationErrors.push(VALIDATION_MESSAGES.PRIMARY_DIFFERENT_FROM_EMAIL);
        }

        // Check each additional email against primary and account
        filledAdditional.forEach(({ email, index }: { email: string; index: number }) => {
          if (primaryNorm && email === primaryNorm) {
            validationErrors.push(
              VALIDATION_MESSAGES.ADDITIONAL_EMAIL_SAME_AS_PRIMARY(index)
            );
          } else if (accountEmailNorm && email === accountEmailNorm) {
            validationErrors.push(
              VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DIFFERENT_FROM_ACCOUNT(index)
            );
          }
        });

        // Check additional emails against each other
        const seenEmails = new Set<string>();
        filledAdditional.forEach(({ email, index }: { email: string; index: number }) => {
          if (seenEmails.has(email)) {
            validationErrors.push(
              VALIDATION_MESSAGES.ADDITIONAL_EMAIL_DUPLICATE(index)
            );
          } else {
            seenEmails.add(email);
          }
        });
      }

      if (formData?.budgetRanges?.length === 0) {
        validationErrors.push(VALIDATION_MESSAGES.BUDGET_RANGE_REQUIRED);
      }

      if (formData?.operatingRegions?.length === 0) {
        validationErrors.push(VALIDATION_MESSAGES.OPERATING_REGION_REQUIRED);
      }


      if (formData.facilityLocationsDetails) {
        const textValidation = validateTextArea(
          formData.facilityLocationsDetails,
          'Facility locations details',
          10,
          2000,
          false
        );
        if (!textValidation.isValid) validationErrors.push(textValidation.error!);
      }

      // FEI Numbers validation and database check (each individual entry)
      if (formData?.feiNumbers) {
        const seenFeis = new Set<string>();
        for (let i = 0; i < formData.feiNumbers.length; i++) {
          const fei = formData.feiNumbers[i]?.trim();
          if (fei) {
            // 1. Format Check
            const feiValidation = validateFeiNumber(fei);
            if (!feiValidation.isValid) {
              validationErrors.push(`FEI Number #${i + 1}: ${feiValidation.error}`);
              continue;
            }
            
            // 2. Internal Duplicate Check
            if (seenFeis.has(fei)) {
              validationErrors.push(`FEI Number #${i + 1}: ${VALIDATION_MESSAGES.FEI_NUMBER_DUPLICATE_IN_FORM}`);
              continue;
            }
            seenFeis.add(fei);

            // 3. Database existence check
            const exists = await dbService.checkFeiNumberExists(fei, cdmoId || adminCreatedCdmoId || undefined);
            if (exists) {
              validationErrors.push(`FEI Number ${fei}: ${VALIDATION_MESSAGES.FEI_NUMBER_ALREADY_REGISTERED}`);
            }
          }
        }
      }

      if (formData?.primaryCapabilities?.length === 0) {
        validationErrors.push(VALIDATION_MESSAGES.PRIMARY_MODALITY_REQUIRED);
      }

      if (formData?.typicalBatchSizes?.length === 0) {
        validationErrors.push(VALIDATION_MESSAGES.BATCH_SIZE_REQUIRED);
      }

      /**
       * Facility records as they will be stored: only regions still selected, free text stripped of
       * markup like every other text field on this form, and bounded.
       *
       * `location` and `equipment` are the only free text that reaches a jsonb column, so they were
       * the only fields here going in unsanitized and unbounded. `capabilities` are canonical labels
       * chosen from a list, not typed.
       */
      const facilitiesForSave = facilitiesInSelectedRegions(
        formData?.facilities,
        formData?.operatingRegions
      ).map(facility => ({
        ...facility,
        location: sanitizeInput(facility.location),
        equipment: sanitizeInput(facility.equipment),
      }));

      facilitiesForSave.forEach(facility => {
        if (facility.location.length > FACILITY_LOCATION_MAX_LENGTH) {
          validationErrors.push(VALIDATION_MESSAGES.FACILITY_LOCATION_TOO_LONG(FACILITY_LOCATION_MAX_LENGTH));
        }
        if (facility.equipment.length > FACILITY_EQUIPMENT_MAX_LENGTH) {
          validationErrors.push(VALIDATION_MESSAGES.FACILITY_EQUIPMENT_TOO_LONG(FACILITY_EQUIPMENT_MAX_LENGTH));
        }
      });

      if (validationErrors.length > 0) {
        throw new Error(`${VALIDATION_MESSAGES.FIX_FOLLOWING_ERRORS}${validationErrors.join(', ')}`);
      }

      const submissionData: any = {
        company_name: sanitizeInput(formData?.companyName) || null,
        contact_name: sanitizeInput(formData?.contactName) || null,
        email: sanitizeInput(formData?.email) || null,
        phone: sanitizeInput(formData?.phone) || null,
        website: sanitizeInput(formData?.website) || null,
        fei_number: (formData?.feiNumbers || []).map(f => sanitizeInput(f.trim())).filter(Boolean).join(',') || null,
        primary_capabilities: formData?.primaryCapabilities || [],
        detailed_capabilities: formData?.detailedCapabilities || {},
        facilities: facilitiesForSave,
        typical_batch_sizes: formData?.typicalBatchSizes || [],
        target_client_types: formData?.targetClientTypes || [],
        engagement_scales: formData?.engagementScales || [],
        preferred_project_types: formData?.preferredProjectTypes || [],
        gmp_certifications: formData?.gmpCertifications || [],
        facility_locations_details: sanitizeInput(formData?.facilityLocationsDetails) || null,
        speed_to_start: formData?.speedToStart || null,
        willingness_feasibility_studies: formData?.willingnessFeasibilityStudies || false,
        budget_ranges: formData?.budgetRanges || [],
        operating_regions: formData?.operatingRegions || [],
        startup_friendly: formData?.startupFriendly || false,
        // Admin fields
        status: formData?.status,
        cdmo_business_profile: formData?.cdmoBusinessProfile,
        primary_email: sanitizeInput(formData?.primaryContactEmail) || null,
        secondary_emails: (formData?.additionalContactEmails || []).filter((email: string) => email?.trim() !== ''),
        admin_notes: sanitizeInput(formData?.adminNotes) || null,
        is_admin_created: isAdmin && mode === 'create'
      };
      if (isAdmin) {
        submissionData.locked_fields = lockedFields;
      }

      let resultApp: any = null;

      if (mode === 'edit') {
        // If cdmoId is provided (admin editing), update by ID
        // Otherwise, update by user ID (user editing their own profile)
        if (cdmoId) {
          resultApp = await dbService.updateCDMOProfileById(cdmoId, submissionData);
        } else if (user.id) {
          await dbService.updateCDMOProfile(user.id, submissionData);
        }
      } else {
        // Defensive check: If adminCreatedCdmoId is null, try one last check for an existing profile
        // This prevents race conditions or failed initial loads from causing duplicate key errors.
        let finalCdmoId = adminCreatedCdmoId;

        if (!finalCdmoId && user?.id) {
          try {
            const existing = await dbService.getCDMOProfile(user.id);
            if (existing) finalCdmoId = existing?.id || null;
          } catch (e) {
            console.warn('Defensive ID check by user failed:', e);
          }
        }

        // If still no ID but we have FEI numbers, no uniqueness check needed (array column, no unique constraint)

        if (finalCdmoId) {
          resultApp = await dbService.updateCDMOProfileById(finalCdmoId, submissionData);
        } else {
          // If admin is creating, don't link it to the admin's user_id
          const targetUserId = (isAdmin && mode === 'create') ? null : user.id;
          resultApp = await dbService.createCDMOApplication({ ...submissionData, user_id: targetUserId });
        }
      }

      // ─── Admin Notification Flow: Claim Invitation ───
      // If admin creates a profile with "Claimed" status, it's saved as "unclaimed" (verified in dbService)
      // and we must send the invite email so the actual user can claim it.
      if (isAdmin && mode === 'create' && formData.status === CDMO_STATUS.CLAIMED && resultApp?.claim_token) {
        try {
          await supabase.functions.invoke('send-admin-created-claimed-notification', {
            body: {
              cdmoId: resultApp?.id,
              cdmoEmail: resultApp?.email || resultApp?.primary_email,
              contactName: resultApp?.contact_name,
              companyName: resultApp?.company_name,
              claimToken: resultApp?.claim_token,
              origin: window.location.origin
            }
          });
        } catch (emailErr) {
          console.error('Failed to send claim invitation email:', emailErr);
          // Note: Profile is still created even if email fails. Admin can resend or check logs.
        }
      }

      setIsSuccess(true);

      // Redirect after a short delay
      setTimeout(() => {
        if (isAdmin) {
          navigate('/matchmaking');
        } else {
          navigate(mode === 'edit' ? '/cdmo-dashboard' : '/matchmaking');
        }
      }, 2000);
    } catch (error: any) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'submitting'} profile:`, error);

      const err = error as { message?: string; code?: string };
      const isFeiUniqueViolation =
        err?.code === '23505' ||
        (typeof err?.message === 'string' && (
          err.message.includes('unique_cdmo_fei_number') ||
          (err.message.toLowerCase().includes('unique') && err.message.toLowerCase().includes('fei'))
        ));

      if (isFeiUniqueViolation) {
        // Try to identify which FEI caused the unique violation
        const feiEntries = formData?.feiNumbers || [];
        let identifiedIndex = -1;
        
        // We perform a quick check to see which one is actually in the DB
        for (let i = 0; i < feiEntries.length; i++) {
          const fei = feiEntries[i]?.trim();
          if (fei) {
            const exists = await dbService.checkFeiNumberExists(fei, cdmoId || adminCreatedCdmoId || undefined);
            if (exists) {
              identifiedIndex = i;
              break;
            }
          }
        }

        const targetIndex = identifiedIndex !== -1 ? identifiedIndex : 0;
        setValidationErrors(prev => ({ 
          ...prev, 
          [`feiNumber_${targetIndex}`]: VALIDATION_MESSAGES.FEI_NUMBER_ALREADY_REGISTERED 
        }));
        setSubmissionError(null);
        // Scroll the specific faulty FEI field into view
        document.getElementById(`feiNumber_${targetIndex}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (err?.message?.includes(VALIDATION_MESSAGES.FIX_FOLLOWING_ERRORS)) {
        setSubmissionError(err.message.replace(VALIDATION_MESSAGES.FIX_FOLLOWING_ERRORS, ''));
      } else {
        setSubmissionError(VALIDATION_MESSAGES.GENERIC_SUBMISSION_ERROR(mode === 'edit' ? 'updating' : 'submitting'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-electric-blue border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-charcoal">
            {mode === 'edit' ? 'Loading your profile data...' : 'Preparing application form...'}
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border border-white/50"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ${mode === 'edit' ? 'bg-gradient-success' : 'bg-gradient-accent'
              }`}
          >
            {mode === 'edit' ? (
              <Check className="w-10 h-10 text-white" />
            ) : (
              <Award className="w-10 h-10 text-white" />
            )}
          </motion.div>

          <h2 className="text-2xl font-bold text-charcoal mb-4">
            {mode === 'edit'
              ? 'Profile Updated!'
              : isAdminCreatedProfile
                ? 'Capabilities Updated!'
                : 'Application Submitted!'}
          </h2>

          <p className="text-slate-gray mb-6">
            {mode === 'edit' ? (
              <>
                Your CDMO profile has been successfully updated. These changes will be reflected across the platform.
                <br />
                <span className="text-sm text-electric-blue font-medium mt-2 block">Redirecting you to your profile...</span>
              </>
            ) : (
              <>
                Thank you for your interest in joining our exclusive CDMO network.
                We're only onboarding select partners this quarter, and we'll review your application within 5 business days.
                <br />
                <span className="text-sm text-electric-blue font-medium mt-2 block">Redirecting you to explore the platform...</span>
              </>
            )}
          </p>

          <div className={`${mode === 'edit' ? 'bg-lime-green' : 'bg-electric-blue'}/10 border ${mode === 'edit' ? 'border-lime-green' : 'border-electric-blue'
            }/30 rounded-xl p-4 mb-6`}>
            <p className={`text-${mode === 'edit' ? 'lime-green' : 'electric-blue'} text-sm font-medium`}>
              {mode === 'edit' ? '✅ Changes saved successfully' : '🎯 Exclusive Partnership Program'}
            </p>
            <p className="text-slate-gray text-sm mt-1">
              {mode === 'edit'
                ? 'Your updated information will be used for future matches'
                : 'You\'re now in consideration for our curated CDMO partner network'}
            </p>
          </div>

          {mode === 'create' && (
            <Link to="/matchmaking">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Explore CDMO Network
              </motion.button>
            </Link>
          )}
        </motion.div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Company Information</h2>
              <p className="text-slate-gray">
                {mode === 'edit' ? 'Update your organization details' : 'Tell us about your organization'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={isAdmin ? "" : "md:col-span-2"}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label htmlFor="companyName" className="block text-sm font-medium text-charcoal">
                    <Building2 className="inline w-4 h-4 mr-1" aria-hidden="true" />
                    Company Name *
                    {isFieldLocked('companyName') && (
                      <Check className="inline w-4 h-4 ml-1 text-green-600" aria-label="Verified" />
                    )}
                  </label>
                  {isAdmin && (
                    <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={isFieldLocked('companyName')}
                        onChange={() => toggleFieldLock('companyName')}
                        className="rounded border-slate-gray/50"
                        aria-label="Verify and lock this field"
                      />
                      <span>Verify & lock</span>
                    </label>
                  )}
                </div>
                <input
                  id="companyName"
                  type="text"
                  name="companyName"
                  required
                  disabled={(!!adminCreatedCdmoId && !isAdmin) || (isFieldLocked('companyName') && !isAdmin)}
                  style={(!!adminCreatedCdmoId && !isAdmin) || (isFieldLocked('companyName') && !isAdmin) ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors.companyName
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-slate-gray/30'
                    }`}
                  placeholder="Enter company name"
                />
                {validationErrors.companyName && (
                  <p className="mt-1 text-xs text-red-500">{validationErrors.companyName}</p>
                )}
                {!validationErrors.companyName && isFieldLocked('companyName') && !isAdmin && (
                  <p className="mt-3 text-xs text-slate-gray" data-testid="locked-disclaimer-companyName">{LOCKED_FIELD_MESSAGE}</p>
                )}
              </div>

              {isAdmin && (
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-charcoal mb-2">
                    Status *
                  </label>
                  <select
                    id="status"
                    name="status"
                    required
                    value={formData?.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80"
                  >
                    <option value={CDMO_STATUS.CLAIMED}>Claimed</option>
                    <option value={CDMO_STATUS.UNCLAIMED}>Unclaimed</option>
                  </select>
                </div>
              )}

              {isAdmin && (
                <div>
                  <label htmlFor="cdmoBusinessProfile" className="block text-sm font-medium text-charcoal mb-2">
                    CDMO Business Profile *
                  </label>
                  <select
                    id="cdmoBusinessProfile"
                    name="cdmoBusinessProfile"
                    required
                    value={formData?.cdmoBusinessProfile}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80"
                  >
                    {CDMO_BUSINESS_PROFILE_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData?.status === CDMO_STATUS.CLAIMED && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label htmlFor="contactName" className="block text-sm font-medium text-charcoal">
                        Contact Name *
                        {isFieldLocked('contactName') && (
                          <Check className="inline w-4 h-4 ml-1 text-green-600" aria-label="Verified" />
                        )}
                      </label>
                      {isAdmin && (
                        <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                          <input type="checkbox" checked={isFieldLocked('contactName')} onChange={() => toggleFieldLock('contactName')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                          <span>Verify & lock</span>
                        </label>
                      )}
                    </div>
                    <input
                      id="contactName"
                      type="text"
                      name="contactName"
                      required
                      disabled={isFieldLocked('contactName') && !isAdmin}
                      style={isFieldLocked('contactName') && !isAdmin ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                      value={formData?.contactName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors?.contactName
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-gray/30'
                        }`}
                      placeholder="Primary contact name"
                    />
                    {validationErrors.contactName && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.contactName}</p>
                    )}
                    {!validationErrors.contactName && isFieldLocked('contactName') && !isAdmin && (
                      <p className="mt-3 text-xs text-slate-gray" data-testid="locked-disclaimer-contactName">{LOCKED_FIELD_MESSAGE}</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label htmlFor="email" className="block text-sm font-medium text-charcoal">
                        Email Address *
                        {isFieldLocked('email') && (
                          <Check className="inline w-4 h-4 ml-1 text-green-600" aria-label="Verified" />
                        )}
                      </label>
                      {isAdmin && (
                        <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                          <input type="checkbox" checked={isFieldLocked('email')} onChange={() => toggleFieldLock('email')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                          <span>Verify & lock</span>
                        </label>
                      )}
                    </div>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      disabled={isFieldLocked('email') && !isAdmin}
                      style={isFieldLocked('email') && !isAdmin ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                      value={formData?.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors?.email
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-gray/30'
                        }`}
                      placeholder="business@company.com"
                    />
                    {validationErrors.email && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.email}</p>
                    )}
                    {!validationErrors.email && isFieldLocked('email') && !isAdmin && (
                      <p className="mt-3 text-xs text-slate-gray" data-testid="locked-disclaimer-email">{LOCKED_FIELD_MESSAGE}</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label htmlFor="phone" className="block text-sm font-medium text-charcoal">
                        Phone Number *
                        {isFieldLocked('phone') && (
                          <Check className="inline w-4 h-4 ml-1 text-green-600" aria-label="Verified" />
                        )}
                      </label>
                      {isAdmin && (
                        <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                          <input type="checkbox" checked={isFieldLocked('phone')} onChange={() => toggleFieldLock('phone')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                          <span>Verify & lock</span>
                        </label>
                      )}
                    </div>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      required
                      disabled={isFieldLocked('phone') && !isAdmin}
                      style={isFieldLocked('phone') && !isAdmin ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                      value={formData?.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors?.phone
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-gray/30'
                        }`}
                      placeholder="+1 (555) 000-0000"
                    />
                    {validationErrors.phone && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.phone}</p>
                    )}
                    {!validationErrors.phone && isFieldLocked('phone') && !isAdmin && (
                      <p className="mt-3 text-xs text-slate-gray" data-testid="locked-disclaimer-phone">{LOCKED_FIELD_MESSAGE}</p>
                    )}
                  </div>
                </>
              )}

              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label htmlFor="website" className="block text-sm font-medium text-charcoal">
                    <Globe className="inline w-4 h-4 mr-1" aria-hidden="true" />
                    Website *
                    {isFieldLocked('website') && (
                      <Check className="inline w-4 h-4 ml-1 text-green-600" aria-label="Verified" />
                    )}
                  </label>
                  {isAdmin && (
                    <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                      <input type="checkbox" checked={isFieldLocked('website')} onChange={() => toggleFieldLock('website')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                      <span>Verify & lock</span>
                    </label>
                  )}
                </div>
                <input
                  id="website"
                  type="url"
                  name="website"
                  required
                  disabled={(!!adminCreatedCdmoId && !isAdmin) || (isFieldLocked('website') && !isAdmin)}
                  style={(!!adminCreatedCdmoId && !isAdmin) || (isFieldLocked('website') && !isAdmin) ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                  value={formData?.website}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors?.website
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-slate-gray/30'
                    }`}
                  placeholder="https://www.company.com"
                />
                {validationErrors.website && (
                  <p className="mt-1 text-xs text-red-500">{validationErrors.website}</p>
                )}
                {!validationErrors.website && isFieldLocked('website') && !isAdmin && (
                  <p className="mt-3 text-xs text-slate-gray" data-testid="locked-disclaimer-website">{LOCKED_FIELD_MESSAGE}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                  <span className="text-sm font-medium text-charcoal">
                    FDA Establishment Identifier (FEI Numbers) <span className="text-xs text-slate-gray">(Optional, up to 10)</span>
                  </span>
                  {isFieldLocked('feiNumbers') && <Check className="inline w-4 h-4 text-green-600" aria-label="Verified" />}
                  {isAdmin && (
                    <label className="inline-flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer">
                      <input type="checkbox" checked={isFieldLocked('feiNumbers')} onChange={() => toggleFieldLock('feiNumbers')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                      <span>Verify & lock</span>
                    </label>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                  {(formData?.feiNumbers || ['']).map((fei, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <input
                          id={`feiNumber_${index}`}
                          type="text"
                          value={fei}
                          disabled={isFieldLocked('feiNumbers') && !isAdmin}
                          style={isFieldLocked('feiNumbers') && !isAdmin ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                          onChange={e => {
                            const val = e.target.value;
                            setFormData(prev => {
                              const updated = [...(prev.feiNumbers || [''])];
                              updated[index] = val;
                              return { ...prev, feiNumbers: updated };
                            });
                            if (val.trim()) {
                              const check = validateFeiNumber(val);
                               setValidationErrors(prev => ({
                                 ...prev,
                                 [`feiNumber_${index}`]: check.isValid ? '' : (check.error || VALIDATION_MESSAGES.FEI_NUMBER_INVALID)
                               }));
                            } else {
                              setValidationErrors(prev => ({ ...prev, [`feiNumber_${index}`]: '' }));
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${
                            validationErrors?.[`feiNumber_${index}`]
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-slate-gray/30'
                          }`}
                          placeholder={`e.g., 1000012345${index > 0 ? ` (FEI #${index + 1})` : ''}`}
                        />
                        {validationErrors?.[`feiNumber_${index}`] ? (
                          <p className="mt-1 text-xs text-red-500" aria-live="polite" data-testid={`fei-error-${index}`}>{validationErrors[`feiNumber_${index}`]}</p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 pt-2 w-10">
                        {/* Delete button - hidden for CDMO when FEI block is admin-verified/locked */}
                        {(formData?.feiNumbers ?? []).length > 1 &&
                        !(isFieldLocked('feiNumbers') && !isAdmin) ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (isFieldLocked('feiNumbers') && !isAdmin) return;
                              const value = (formData?.feiNumbers?.[index] ?? '').trim();
                              if (value === '') {
                                performDeleteFeiAtIndex(index);
                              } else {
                                setDeleteFeiIndex(index);
                              }
                            }}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200"
                            title="Remove FEI number"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Add More - always left-aligned below the FEI fields */}
                {(formData?.feiNumbers ?? []).length < 10 && !(isFieldLocked('feiNumbers') && !isAdmin) && (
                  <div className="mt-3 flex justify-start">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, feiNumbers: [...(prev.feiNumbers || ['']), ''] }));
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-electric-blue/10 text-electric-blue hover:bg-electric-blue/20 rounded-xl transition-all duration-200 font-medium text-sm"
                      title="Add another FEI number"
                    >
                      <Plus className="w-4 h-4" aria-hidden="true" />
                      Add More
                    </button>
                  </div>
                )}
                {isFieldLocked('feiNumbers') && !isAdmin && (
                  <p className="mt-3 text-xs text-slate-gray" data-testid="locked-disclaimer-feiNumbers">{LOCKED_FIELD_MESSAGE}</p>
                )}
              </div>

              {isAdmin && (
                <div className="md:col-span-1">
                  <label htmlFor="primaryContactEmail" className="block text-sm font-medium text-charcoal mb-2">
                    Primary Contact Email {formData?.status === CDMO_STATUS.UNCLAIMED ? '*' : ''}
                  </label>
                  <input
                    id="primaryContactEmail"
                    type="email"
                    name="primaryContactEmail"
                    required={formData?.status === CDMO_STATUS.UNCLAIMED}
                    value={formData?.primaryContactEmail}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors?.primaryContactEmail ? 'border-red-300 focus:ring-red-500' : 'border-slate-gray/30'}`}
                    placeholder="Primary intake email"
                  />
                  {validationErrors?.primaryContactEmail && (
                    <p className="mt-1 text-xs text-red-500">{validationErrors.primaryContactEmail}</p>
                  )}
                </div>
              )}
            </div>

            {
              isAdmin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-6 pt-2"
                >
                  <div>
                    {/* <h3 className="text-lg font-bold text-charcoal mb-4">
                    Admin Configuration
                  </h3> */}

                    <div className="space-y-4">
                      {/* <label className="block text-sm font-medium text-charcoal mb-2">
                        Additional Contact Emails
                      </label> */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {formData?.additionalContactEmails?.map((email, index) => (
                          <div key={`additional-email-${index}`}>
                            <label htmlFor={`additionalEmail_${index}`} className="block text-sm font-medium text-charcoal mb-2">
                              Additional Contact Email {index + 2} <span className="text-xs text-slate-gray">(Optional)</span>
                            </label>
                            <input
                              id={`additionalEmail_${index}`}
                              type="email"
                              value={email}
                              onChange={(e) => handleAdditionalEmailChange(index, e.target.value)}
                              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 ${validationErrors[`additionalEmail_${index}`]
                                ? 'border-red-300 focus:ring-red-500'
                                : 'border-slate-gray/30'
                                }`}
                              placeholder={`contact${index + 2}@company.com`}
                            />
                            {validationErrors[`additionalEmail_${index}`] && (
                              <p className="mt-1 text-xs text-red-500">{validationErrors[`additionalEmail_${index}`]}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="adminNotes" className="block text-sm font-medium text-charcoal mb-2">
                      Admin Notes (Optional)
                    </label>
                    <textarea
                      id="adminNotes"
                      name="adminNotes"
                      rows={4}
                      value={formData?.adminNotes}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80"
                      placeholder="Internal notes about this CDMO..."
                    />
                  </div>
                </motion.div>
              )
            }
          </motion.div >
        );

      case 2:
        return (
          <motion.div
            key="step2"
            id="primaryCapabilities"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl font-bold text-charcoal mb-2">Primary Modalities</h2>
                <p className="text-slate-gray">
                  {mode === 'edit'
                    ? 'Update your main therapeutic areas and manufacturing capabilities'
                    : 'Select your main therapeutic areas and manufacturing capabilities'}
                </p>
              </div>
              {isAdmin && (
                <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                  <input type="checkbox" checked={isFieldLocked('primaryCapabilities')} onChange={() => toggleFieldLock('primaryCapabilities')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                  <span>Verify & lock</span>
                  {isFieldLocked('primaryCapabilities') && <Check className="w-4 h-4 text-green-600" aria-label="Verified" />}
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PRIMARY_CAPABILITIES_OPTIONS.map(capability => (
                <motion.label
                  key={capability}
                  whileHover={!(isFieldLocked('primaryCapabilities') && !isAdmin) ? { scale: 1.02 } : {}}
                  whileTap={!(isFieldLocked('primaryCapabilities') && !isAdmin) ? { scale: 0.98 } : {}}
                  className={`flex items-center p-6 border-2 rounded-xl transition-all duration-300 ${(isFieldLocked('primaryCapabilities') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.primaryCapabilities?.includes(capability)
                    ? SELECTED_TILE_CLASS
                    : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white/80'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={formData?.primaryCapabilities?.includes(capability)}
                    onChange={() => handlePrimaryCapabilityToggle(capability)}
                    disabled={isFieldLocked('primaryCapabilities') && !isAdmin}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <FlaskConical className="w-5 h-5 mr-3 text-electric-blue" />
                    <span className="font-medium">{getPrimaryCapabilityLabel(capability)}</span>
                  </div>
                </motion.label>
              ))}
            </div>

            {formData?.primaryCapabilities?.length === 0 && (
              <div className="bg-sky-blue/10 border border-sky-blue/30 rounded-xl p-4">
                <p className="text-sky-blue text-sm">
                  💡 Select at least one primary modality to proceed to Primary Capabilities
                </p>
                {validationErrors.primaryCapabilities && (
                  <p className="mt-2 text-sm text-red-500 font-medium">
                    {validationErrors.primaryCapabilities}
                  </p>
                )}
              </div>
            )}
            {isFieldLocked('primaryCapabilities') && !isAdmin && (
              <p className="mt-2 text-xs text-slate-gray" data-testid="locked-disclaimer-primaryCapabilities">{LOCKED_FIELD_MESSAGE}</p>
            )}
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Primary Capabilities</h2>
              <p className="text-slate-gray">Specify the detailed services you provide within each selected area</p>
            </div>

            {formData?.primaryCapabilities?.length === 0 ? (
              <div className="bg-slate-gray/10 border border-slate-gray/30 rounded-xl p-8 text-center">
                <Target className="w-12 h-12 text-slate-gray mx-auto mb-4" aria-hidden="true" />
                <p className="text-slate-gray">
                  Please select primary modalities in the previous step to see detailed options
                </p>
              </div>
            ) : (
              formData?.primaryCapabilities?.map(primaryCapability => {
                const detailedLockKey = `detailedCapabilities.${primaryCapability}`;
                const isThisCardLocked = isFieldLocked('detailedCapabilities') || isFieldLocked(detailedLockKey);
                return (
                  <div key={primaryCapability} className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <h3 className="text-lg font-semibold text-charcoal flex items-center">
                        <FlaskConical className="w-5 h-5 mr-2 text-electric-blue" />
                        {getPrimaryCapabilityLabel(primaryCapability)}
                        {isThisCardLocked && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                      </h3>
                      {isAdmin && (
                        <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={isFieldLocked(detailedLockKey)}
                            onChange={() => toggleFieldLock(detailedLockKey)}
                            className="rounded border-slate-gray/50"
                            aria-label={`Verify and lock ${getPrimaryCapabilityLabel(primaryCapability)}`}
                          />
                          <span>Verify & lock</span>
                        </label>
                      )}
                    </div>
                    {(() => {
                      const searchTerm = capabilitySearch[primaryCapability] ?? '';
                      const { groups: allGroups, totalCount } =
                        capabilityGroupsByModality.get(primaryCapability) ?? { groups: [], totalCount: 0 };
                      const visibleGroups = filterCapabilityGroups(allGroups, searchTerm);
                      const visibleCount = countCapabilityOptions(visibleGroups);
                      const selectedForModality = formData?.detailedCapabilities?.[primaryCapability] ?? [];
                      const searchInputId = `capability-search-${primaryCapability}`;

                      return (
                        <>
                          <div className="relative mb-4">
                            <Search className="w-4 h-4 text-slate-gray absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
                            <input
                              id={searchInputId}
                              type="text"
                              value={searchTerm}
                              onChange={(e) => setCapabilitySearch(prev => ({ ...prev, [primaryCapability]: e.target.value }))}
                              placeholder={`Search ${getPrimaryCapabilityLabel(primaryCapability)} capabilities...`}
                              aria-label={`Search ${getPrimaryCapabilityLabel(primaryCapability)} capabilities`}
                              className="w-full pl-10 pr-4 py-2.5 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm bg-white/80"
                            />
                          </div>

                          <p className="text-xs text-slate-gray mb-4">
                            {searchTerm.trim()
                              ? `${visibleCount} of ${totalCount} capabilities match "${searchTerm.trim()}"`
                              : `${selectedForModality.length} of ${totalCount} selected`}
                          </p>

                          {visibleGroups.length === 0 ? (
                            <p className="text-sm text-slate-gray text-center py-6 border border-dashed border-slate-gray/30 rounded-lg">
                              No {getPrimaryCapabilityLabel(primaryCapability)} capabilities match "{searchTerm.trim()}".
                            </p>
                          ) : (
                            visibleGroups.map(({ group, options }) => {
                              const selectedInGroup = options.filter(option => selectedForModality.includes(option)).length;
                              return (
                                <div key={group || 'ungrouped'} className="mb-5 last:mb-0">
                                  {group && (
                                    <div className="flex items-baseline gap-2 mb-2.5 pb-1.5 border-b border-slate-gray/15">
                                      <h4 className="text-xs font-bold tracking-wide uppercase text-slate-gray">{group}</h4>
                                      <span className="text-xs text-slate-gray">
                                        {selectedInGroup > 0 && `${selectedInGroup} selected · `}
                                        {options.length} option{options.length === 1 ? '' : 's'}
                                      </span>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {options.map(detailedCapability => (
                                      <motion.label
                                        key={detailedCapability}
                                        whileHover={!(isThisCardLocked && !isAdmin) ? { scale: 1.02 } : {}}
                                        whileTap={!(isThisCardLocked && !isAdmin) ? { scale: 0.98 } : {}}
                                        className={`flex items-center p-3 border rounded-lg transition-all duration-300 text-sm ${(isThisCardLocked && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData.detailedCapabilities[primaryCapability]?.includes(detailedCapability)
                                          ? SELECTED_TILE_CLASS
                                          : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                                          }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={formData?.detailedCapabilities?.[primaryCapability]?.includes(detailedCapability) || false}
                                          onChange={() => handleDetailedCapabilityToggle(primaryCapability, detailedCapability)}
                                          disabled={isThisCardLocked && !isAdmin}
                                          className="sr-only"
                                        />
                                        <span className="font-medium">{detailedCapability}</span>
                                      </motion.label>
                                    ))}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </>
                      );
                    })()}
                    {isThisCardLocked && !isAdmin && (
                      <p className="mt-3 text-xs text-slate-gray" data-testid={`locked-disclaimer-${detailedLockKey}`}>{LOCKED_FIELD_MESSAGE}</p>
                    )}
                  </div>
                );
              })
            )}
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Client Fit & Preferences</h2>
              <p className="text-slate-gray">Help us understand your ideal client partnerships</p>
            </div>

            {/* Typical Batch Sizes */}
            <div id="typicalBatchSizes" className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center flex-wrap gap-x-2">
                  <Target className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Typical Batch Sizes
                  {(() => {
                    const hasSmallMolecule = formData?.primaryCapabilities?.includes('Small Molecule');
                    const hasBiologics = formData?.primaryCapabilities?.includes('Biologics');
                    if (hasSmallMolecule && hasBiologics) return <span className="text-sm text-slate-gray">(kg for Small Molecule / L for Biologics)</span>;
                    if (hasBiologics) return <span className="text-sm text-slate-gray">(Liters)</span>;
                    return <span className="text-sm text-slate-gray">(Kilograms)</span>;
                  })()}
                  {isFieldLocked('typicalBatchSizes') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('typicalBatchSizes')} onChange={() => toggleFieldLock('typicalBatchSizes')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>

              {formData?.primaryCapabilities?.length === 0 && (
                <div className="bg-sky-blue/10 border border-sky-blue/30 rounded-lg p-3 mb-4">
                  <p className="text-sky-blue text-sm">
                    💡 Select primary modalities first to see appropriate batch size units
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(() => {
                  const hasSmallMolecule = formData?.primaryCapabilities?.includes('Small Molecule');
                  const hasBiologics = formData?.primaryCapabilities?.includes('Biologics');

                  let batchSizeOptions;
                  if (hasSmallMolecule && hasBiologics) {
                    batchSizeOptions = BATCH_SIZES_COMBINED_OPTIONS;
                  } else if (hasBiologics) {
                    batchSizeOptions = BATCH_SIZES_L_OPTIONS;
                  } else {
                    batchSizeOptions = BATCH_SIZES_KG_OPTIONS;
                  }

                  return batchSizeOptions;
                })().map(size => (
                  <motion.label
                    key={size}
                    whileHover={!(isFieldLocked('typicalBatchSizes') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('typicalBatchSizes') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('typicalBatchSizes') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.typicalBatchSizes?.includes(size)
                      ? SELECTED_TILE_CLASS
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.typicalBatchSizes?.includes(size)}
                      onChange={() => handleMultiSelectToggle('typicalBatchSizes', size)}
                      disabled={isFieldLocked('typicalBatchSizes') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{size}</span>
                  </motion.label>
                ))}
              </div>
              {validationErrors.typicalBatchSizes && (
                <p className="mt-4 text-sm text-red-500 font-medium">
                  {validationErrors.typicalBatchSizes}
                </p>
              )}
              {isFieldLocked('typicalBatchSizes') && !isAdmin && (
                <p className="mt-1 text-xs text-slate-gray" data-testid="locked-disclaimer-typicalBatchSizes">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Typical Project Budgets */}
            <div id="budgetRanges" className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Target className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Typical Project Budgets You Support
                  {isFieldLocked('budgetRanges') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('budgetRanges')} onChange={() => toggleFieldLock('budgetRanges')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {BUDGET_RANGES_OPTIONS?.map(budget => (
                  <motion.label
                    key={budget}
                    whileHover={!(isFieldLocked('budgetRanges') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('budgetRanges') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('budgetRanges') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.budgetRanges?.includes(budget)
                      ? SELECTED_TILE_CLASS
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.budgetRanges?.includes(budget)}
                      onChange={() => handleMultiSelectToggle('budgetRanges', budget)}
                      disabled={isFieldLocked('budgetRanges') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{budget}</span>
                  </motion.label>
                ))}
              </div>
              {validationErrors.budgetRanges && (
                <p className="mt-4 text-sm text-red-500 font-medium">
                  {validationErrors.budgetRanges}
                </p>
              )}
              {isFieldLocked('budgetRanges') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-budgetRanges">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Target Client Types */}
            <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Users className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Preferred Client Types
                  {isFieldLocked('targetClientTypes') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('targetClientTypes')} onChange={() => toggleFieldLock('targetClientTypes')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CLIENT_TYPES_OPTIONS.map(clientType => (
                  <motion.label
                    key={clientType}
                    whileHover={!(isFieldLocked('targetClientTypes') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('targetClientTypes') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('targetClientTypes') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.targetClientTypes?.includes(clientType)
                      ? SELECTED_TILE_CLASS
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.targetClientTypes?.includes(clientType)}
                      onChange={() => handleMultiSelectToggle('targetClientTypes', clientType)}
                      disabled={isFieldLocked('targetClientTypes') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{clientType}</span>
                  </motion.label>
                ))}
              </div>
              {isFieldLocked('targetClientTypes') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-targetClientTypes">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Engagement Scales */}
            <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <FlaskConical className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Development Stages You Support
                  {isFieldLocked('engagementScales') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('engagementScales')} onChange={() => toggleFieldLock('engagementScales')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ENGAGEMENT_SCALES_OPTIONS?.map(scale => (
                  <motion.label
                    key={scale}
                    whileHover={!(isFieldLocked('engagementScales') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('engagementScales') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('engagementScales') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.engagementScales?.includes(scale)
                      ? SELECTED_TILE_CLASS
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.engagementScales?.includes(scale)}
                      onChange={() => handleMultiSelectToggle('engagementScales', scale)}
                      disabled={isFieldLocked('engagementScales') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{scale}</span>
                  </motion.label>
                ))}
              </div>
              {isFieldLocked('engagementScales') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-engagementScales">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Preferred Project Types */}
            <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Preferred Project Types
                  {isFieldLocked('preferredProjectTypes') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('preferredProjectTypes')} onChange={() => toggleFieldLock('preferredProjectTypes')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PROJECT_TYPES_OPTIONS.map(projectType => (
                  <motion.label
                    key={projectType}
                    whileHover={!(isFieldLocked('preferredProjectTypes') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('preferredProjectTypes') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('preferredProjectTypes') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.preferredProjectTypes?.includes(projectType)
                      ? SELECTED_TILE_CLASS
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.preferredProjectTypes?.includes(projectType)}
                      onChange={() => handleMultiSelectToggle('preferredProjectTypes', projectType)}
                      disabled={isFieldLocked('preferredProjectTypes') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{projectType}</span>
                  </motion.label>
                ))}
              </div>
              {isFieldLocked('preferredProjectTypes') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-preferredProjectTypes">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            key="step5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Operational Details</h2>
              <p className="text-slate-gray">Provide key operational information about your facilities and processes</p>
            </div>

            {/* GMP Certifications */}
            <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  GMP Certifications & Compliance
                  {isFieldLocked('gmpCertifications') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('gmpCertifications')} onChange={() => toggleFieldLock('gmpCertifications')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {GMP_CERTIFICATIONS_OPTIONS?.map(certification => (
                  <motion.label
                    key={certification}
                    whileHover={!(isFieldLocked('gmpCertifications') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('gmpCertifications') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('gmpCertifications') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.gmpCertifications?.includes(certification)
                      ? SELECTED_TILE_CLASS
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.gmpCertifications?.includes(certification)}
                      onChange={() => handleMultiSelectToggle('gmpCertifications', certification)}
                      disabled={isFieldLocked('gmpCertifications') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{certification}</span>
                  </motion.label>
                ))}
              </div>
              {isFieldLocked('gmpCertifications') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-gmpCertifications">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Operating Regions */}
            <div id="operatingRegions" className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Globe className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Operating Regions / Facility Locations
                  {isFieldLocked('operatingRegions') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('operatingRegions')} onChange={() => toggleFieldLock('operatingRegions')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {OPERATING_REGIONS_OPTIONS?.map(region => (
                  <motion.label
                    key={region}
                    whileHover={!(isFieldLocked('operatingRegions') && !isAdmin) ? { scale: 1.02 } : {}}
                    whileTap={!(isFieldLocked('operatingRegions') && !isAdmin) ? { scale: 0.98 } : {}}
                    className={`flex items-center p-3 border rounded-lg transition-all duration-300 ${(isFieldLocked('operatingRegions') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} ${formData?.operatingRegions?.includes(region)
                      ? 'border-selected-blue bg-selected-blue/10 text-selected-blue'
                      : 'border-slate-gray/30 hover:border-electric-blue/50 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData?.operatingRegions?.includes(region)}
                      onChange={() => handleMultiSelectToggle('operatingRegions', region)}
                      disabled={isFieldLocked('operatingRegions') && !isAdmin}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{region}</span>
                  </motion.label>
                ))}
              </div>
              {validationErrors.operatingRegions && (
                <p className="mt-4 text-sm text-red-500 font-medium">
                  {validationErrors.operatingRegions}
                </p>
              )}
              {isFieldLocked('operatingRegions') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-operatingRegions">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Facility Details — optional, driven by the regions selected above */}
            <div id="facilities" className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Factory className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Facility Details <span className="ml-1.5 text-sm font-medium text-slate-gray">(optional)</span>
                  {isFieldLocked('facilities') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('facilities')} onChange={() => toggleFieldLock('facilities')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify &amp; lock</span>
                  </label>
                )}
              </div>
              <p className="text-sm text-slate-gray mb-4">
                Add facility locations, capabilities, and manufacturing equipment for each region you operate in. Every field is optional.
              </p>

              {(formData?.operatingRegions?.length ?? 0) === 0 ? (
                <p className="text-sm text-slate-gray text-center py-6 border border-dashed border-slate-gray/30 rounded-lg">
                  Select an operating region above to add facility details.
                </p>
              ) : (
                // Ordered by OPERATING_REGIONS_OPTIONS rather than selection order, so the sections
                // keep a stable position as regions are ticked and unticked.
                OPERATING_REGIONS_OPTIONS.filter(region => formData?.operatingRegions?.includes(region)).map(region => {
                  const regionFacilities = (formData?.facilities ?? []).filter(facility => facility.region === region);
                  return (
                    <div key={region} className="mb-6 last:mb-0 pt-4 first:pt-0 border-t first:border-t-0 border-dashed border-slate-gray/25">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-selected-blue" aria-hidden="true" />
                        <h4 className="text-xs font-bold tracking-wide uppercase text-selected-blue">{region}</h4>
                        <span className="text-xs text-slate-gray">
                          · {regionFacilities.length} facilit{regionFacilities.length === 1 ? 'y' : 'ies'}
                        </span>
                      </div>

                      {regionFacilities.map((facility, index) => (
                        <div key={facility.id} className="border border-slate-gray/20 rounded-lg p-4 bg-light-gray/60 mb-3">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold tracking-wide uppercase text-slate-gray">
                              Facility {index + 1}
                            </span>
                            {!(isFieldLocked('facilities') && !isAdmin) && (
                              <button
                                type="button"
                                onClick={() => handleRemoveFacility(facility.id)}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-gray hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="mb-3">
                            <label htmlFor={`facility-location-${facility.id}`} className="block text-sm font-medium text-charcoal mb-1.5">
                              Facility location <span className="font-normal text-slate-gray">(optional)</span>
                            </label>
                            <input
                              id={`facility-location-${facility.id}`}
                              type="text"
                              value={facility.location}
                              onChange={(e) => handleFacilityChange(facility.id, 'location', e.target.value)}
                              disabled={isFieldLocked('facilities') && !isAdmin}
                              maxLength={FACILITY_LOCATION_MAX_LENGTH}
                              placeholder="City, state / province, country"
                              className="w-full px-4 py-2.5 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm bg-white"
                            />
                          </div>

                          <div className="mb-3">
                            <span className="block text-sm font-medium text-charcoal mb-1.5">
                              Capabilities available at this facility <span className="font-normal text-slate-gray">(optional)</span>
                            </span>
                            <FacilityCapabilitySelect
                              profileCapabilities={formData?.detailedCapabilities ?? {}}
                              value={facility.capabilities}
                              onChange={(next) => handleFacilityChange(facility.id, 'capabilities', next)}
                              disabled={isFieldLocked('facilities') && !isAdmin}
                              ariaLabel={`Capabilities available at ${region} Facility ${index + 1}`}
                            />
                            <p className="mt-1.5 text-xs text-slate-gray">
                              Select from the primary capabilities already included in your CDMO profile.
                            </p>
                          </div>

                          <div>
                            <label htmlFor={`facility-equipment-${facility.id}`} className="block text-sm font-medium text-charcoal mb-1.5">
                              Manufacturing equipment <span className="font-normal text-slate-gray">(optional)</span>
                            </label>
                            <textarea
                              id={`facility-equipment-${facility.id}`}
                              value={facility.equipment}
                              onChange={(e) => handleFacilityChange(facility.id, 'equipment', e.target.value)}
                              disabled={isFieldLocked('facilities') && !isAdmin}
                              maxLength={FACILITY_EQUIPMENT_MAX_LENGTH}
                              rows={3}
                              placeholder="e.g., spray dryer, 500 L bioreactor, rotary tablet press, fluid-bed granulator, vial filling line, lyophilizer"
                              className="w-full px-4 py-2.5 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm bg-white resize-y"
                            />
                            <p className="mt-1.5 text-xs text-slate-gray">Separate entries with commas or new lines.</p>
                          </div>
                        </div>
                      ))}

                      {!(isFieldLocked('facilities') && !isAdmin) && (
                        <button
                          type="button"
                          onClick={() => handleAddFacility(region)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-selected-blue/50 bg-selected-blue/5 text-selected-blue text-sm font-medium hover:bg-selected-blue/10 transition-colors"
                        >
                          <Plus className="w-4 h-4" aria-hidden="true" />
                          Add another facility
                        </button>
                      )}
                    </div>
                  );
                })
              )}

              {isFieldLocked('facilities') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-facilities">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Project Kickoff */}
            <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-6">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-lg font-semibold text-charcoal flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-electric-blue" aria-hidden="true" />
                  Typical Time to Project Kickoff
                  {isFieldLocked('speedToStart') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0">
                    <input type="checkbox" checked={isFieldLocked('speedToStart')} onChange={() => toggleFieldLock('speedToStart')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <p id="speedToStart-hint" className="text-sm text-slate-gray mb-4">
                Typical time after project selection and required agreements are completed for the project team to begin kickoff.
              </p>
              <select
                id="speedToStart"
                name="speedToStart"
                value={formData?.speedToStart}
                onChange={handleInputChange}
                aria-label="Typical Time to Project Kickoff"
                aria-describedby="speedToStart-hint"
                disabled={isFieldLocked('speedToStart') && !isAdmin}
                style={isFieldLocked('speedToStart') && !isAdmin ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80"
              >
                <option value="">Select typical timeline</option>
                {SPEED_TO_START_OPTIONS?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {isFieldLocked('speedToStart') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-speedToStart">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            {/* Feasibility Studies */}
            <div className="bg-lime-green/10 border border-lime-green/30 rounded-xl p-6 relative">
              {/* Top row: title on left, Verify & lock anchored top-right */}
              <div className="flex items-start justify-between gap-4 w-full">
                <h3 className="font-semibold text-charcoal flex items-center gap-x-1 min-w-0 flex-1 pr-3">
                  <FlaskConical className="w-5 h-5 shrink-0 text-lime-green" aria-hidden="true" />
                  <span>Feasibility Studies & Exploratory Work</span>
                  {isFieldLocked('willingnessFeasibilityStudies') && <Check className="w-4 h-4 shrink-0 text-green-600" aria-label="Verified" />}
                </h3>
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer shrink-0 whitespace-nowrap border border-slate-gray/20 rounded-md px-2.5 py-1.5 bg-white/60 hover:bg-white/80 transition-colors">
                    <input type="checkbox" checked={isFieldLocked('willingnessFeasibilityStudies')} onChange={() => toggleFieldLock('willingnessFeasibilityStudies')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                    <span>Verify & lock</span>
                  </label>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 mt-2 flex-wrap">
                <p className="text-sm text-slate-gray flex-1 min-w-0">
                  Are you willing to conduct feasibility studies or exploratory work before full project commitment?
                </p>
                <motion.label
                  whileTap={!(isFieldLocked('willingnessFeasibilityStudies') && !isAdmin) ? { scale: 0.95 } : {}}
                  className={`relative inline-flex shrink-0 items-center ${(isFieldLocked('willingnessFeasibilityStudies') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    id="willingnessFeasibilityStudies"
                    name="willingnessFeasibilityStudies"
                    checked={formData?.willingnessFeasibilityStudies}
                    onChange={handleInputChange}
                    disabled={isFieldLocked('willingnessFeasibilityStudies') && !isAdmin}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-gray/30 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lime-green/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-gray/30 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-green"></div>
                </motion.label>
              </div>
              {isFieldLocked('willingnessFeasibilityStudies') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-willingnessFeasibilityStudies">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>
          </motion.div>
        );

      case 6:
        return (
          <motion.div
            key="step6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Final Details</h2>
              <p className="text-slate-gray">Complete your application with these final details</p>
            </div>

            <div className="bg-lime-green/10 border border-lime-green/30 rounded-xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold text-charcoal mb-1 flex items-center">
                    Startup Friendly?
                    {isFieldLocked('startupFriendly') && <Check className="w-4 h-4 ml-1 text-green-600" aria-label="Verified" />}
                  </h3>
                  <p className="text-sm text-slate-gray">
                    Are you open to working with early-stage companies with smaller batch sizes?
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <label className="flex items-center gap-1.5 text-sm text-slate-gray cursor-pointer">
                      <input type="checkbox" checked={isFieldLocked('startupFriendly')} onChange={() => toggleFieldLock('startupFriendly')} className="rounded border-slate-gray/50" aria-label="Verify and lock this field" />
                      <span>Verify & lock</span>
                    </label>
                  )}
                  <motion.label
                    whileTap={!(isFieldLocked('startupFriendly') && !isAdmin) ? { scale: 0.95 } : {}}
                    className={`relative inline-flex items-center ${(isFieldLocked('startupFriendly') && !isAdmin) ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      id="startupFriendly"
                      name="startupFriendly"
                      checked={formData?.startupFriendly}
                      onChange={handleInputChange}
                      disabled={isFieldLocked('startupFriendly') && !isAdmin}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-gray/30 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lime-green/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-gray/30 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-green"></div>
                  </motion.label>
                </div>
              </div>
              {isFieldLocked('startupFriendly') && !isAdmin && (
                <p className="mt-4 text-xs text-slate-gray" data-testid="locked-disclaimer-startupFriendly">{LOCKED_FIELD_MESSAGE}</p>
              )}
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-bold text-charcoal mb-2">
                {isAdminCreatedProfile && mode === 'create' ? 'Review & Update' : 'Review & Submit'}
              </h2>
              <p className="text-slate-gray">
                {isAdminCreatedProfile && mode === 'create'
                  ? 'Please review your capabilities before updating'
                  : 'Please review your application before submitting'}
              </p>
            </div>

            <div className="bg-gradient-to-r from-electric-blue/10 to-soft-teal/10 border border-electric-blue/30 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <Award className="w-6 h-6 text-electric-blue mr-2" aria-hidden="true" />
                <h3 className="font-semibold text-deep-indigo">Exclusive Partnership Program</h3>
              </div>
              <p className="text-slate-gray text-sm">
                We're only onboarding select CDMOs this quarter to ensure quality partnerships.
                Your application will be reviewed within 5 business days.
              </p>
            </div>

            <div className="space-y-4">
              {/* Company Information */}
              <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-4">
                <h4 className="font-medium text-charcoal mb-2">Company Information</h4>
                <div className="text-sm text-slate-gray space-y-1">
                  <p><strong>Status:</strong> <span className="capitalize">{formData.status}</span></p>
                  <p><strong>Company:</strong> {formData.companyName}</p>
                  <p><strong>Contact:</strong> {formData.contactName || '-'}</p>
                  <p><strong>Email:</strong> {formData.email || '-'}</p>
                  <p><strong>Phone:</strong> {formData.phone || '-'}</p>
                  {formData.website && <p><strong>Website:</strong> {formData.website}</p>}
                  {(formData.feiNumbers || []).filter(f => f.trim()).length > 0 && (
                    <p><strong>FEI Numbers:</strong> {(formData.feiNumbers || []).filter(f => f.trim()).join(', ')}</p>
                  )}

                  {isAdmin && (
                    <div className="text-sm text-slate-gray space-y-1">
                      {/* <p className="font-medium text-charcoal">Admin Details:</p> */}
                      {formData.primaryContactEmail && (
                        <p><strong>Primary Contact Email:</strong> {formData.primaryContactEmail}</p>
                      )}
                      {formData.additionalContactEmails.filter(e => e.trim()).length > 0 && (
                        <p><strong>Additional Emails:</strong> {formData.additionalContactEmails.filter(e => e.trim()).join(', ')}</p>
                      )}
                      {formData.adminNotes && (
                        <p><strong>Admin Notes:</strong> {formData.adminNotes}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Primary Modalities */}
              <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-4">
                <h4 className="font-medium text-charcoal mb-2">Primary Modalities</h4>
                <div className="flex flex-wrap gap-2">
                  {formData?.primaryCapabilities?.map(cap => (
                    <span key={cap} className="px-3 py-1 bg-electric-blue/10 text-deep-indigo text-sm rounded-lg border border-electric-blue/20">
                      {getPrimaryCapabilityLabel(cap)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Primary Capabilities */}
              {Object.keys(formData?.detailedCapabilities).length > 0 && (
                <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-4">
                  <h4 className="font-medium text-charcoal mb-2">Primary Capabilities</h4>
                  {Object.entries(formData?.detailedCapabilities).map(([primary, detailed]) => (
                    <div key={primary} className="mb-3 last:mb-0">
                      <p className="text-sm font-medium text-charcoal mb-1">{getPrimaryCapabilityLabel(primary)}:</p>
                      <div className="flex flex-wrap gap-1">
                        {detailed?.map(detail => (
                          <span key={detail} className="px-2 py-1 bg-sky-blue/10 text-deep-indigo text-xs rounded border border-sky-blue/20">
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Client Fit */}
              <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-4">
                <h4 className="font-medium text-charcoal mb-2">Client Fit & Preferences</h4>
                <div className="text-sm text-slate-gray space-y-2">
                  {formData?.typicalBatchSizes?.length > 0 && (
                    <div>
                      <strong>Batch Sizes:</strong> {formData?.typicalBatchSizes?.join(', ')}
                    </div>
                  )}
                  {formData?.targetClientTypes?.length > 0 && (
                    <div>
                      <strong>Client Types:</strong> {formData?.targetClientTypes?.join(', ')}
                    </div>
                  )}
                  {formData?.engagementScales?.length > 0 && (
                    <div>
                      <strong>Development Stages:</strong> {formData?.engagementScales?.join(', ')}
                    </div>
                  )}
                  {formData?.preferredProjectTypes?.length > 0 && (
                    <div>
                      <strong>Project Types:</strong> {formData?.preferredProjectTypes?.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Operational Details */}
              <div className="bg-white/80 border border-slate-gray/20 rounded-xl p-4">
                <h4 className="font-medium text-charcoal mb-2">Operational Details</h4>
                <div className="text-sm text-slate-gray space-y-2">
                  {formData?.gmpCertifications?.length > 0 && (
                    <div>
                      <strong>GMP Certifications:</strong> {formData?.gmpCertifications?.join(', ')}
                    </div>
                  )}
                  {formData?.facilityLocationsDetails && (
                    <div>
                      <strong>Facility Locations:</strong> {formData?.facilityLocationsDetails}
                    </div>
                  )}
                  {/* Counts what will actually be saved. Records left behind by an unticked region
                      are still in state but have no section to render in, so including them reported
                      facilities in a region the CDMO no longer operates in. */}
                  {(() => {
                    const savedFacilities = facilitiesInSelectedRegions(
                      formData?.facilities,
                      formData?.operatingRegions
                    );
                    if (savedFacilities.length === 0) return null;
                    const regionCount = new Set(savedFacilities.map(facility => facility.region)).size;
                    return (
                      <div>
                        <strong>Facility Details:</strong>{' '}
                        {savedFacilities.length} facilit{savedFacilities.length === 1 ? 'y' : 'ies'} across{' '}
                        {regionCount} region{regionCount === 1 ? '' : 's'}
                      </div>
                    );
                  })()}
                  {formData?.speedToStart && (
                    <div>
                      <strong>Project Kickoff:</strong> {formData?.speedToStart}
                    </div>
                  )}
                  <div>
                    <strong>Feasibility Studies:</strong> {formData?.willingnessFeasibilityStudies ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <strong>Startup Friendly:</strong> {formData?.startupFriendly ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-electric-blue/20 to-deep-indigo/15 backdrop-blur-sm border-b border-electric-blue/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link
            to={mode === 'edit' ? '/cdmo-dashboard' : '/'}
            className="inline-flex items-center text-electric-blue hover:text-deep-indigo transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            {mode === 'edit' ? 'Back to CDMO Dashboard' : 'Back to Home'}
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center flex-1 min-w-0">
              <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
                <Building2 className="w-6 h-6 text-white" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-charcoal">
                  {mode === 'edit' ? 'Edit CDMO Profile' : 'CDMO Application'}
                </h1>
                <p className="text-sm sm:text-base text-slate-gray">
                  {mode === 'edit'
                    ? 'Update your company information and capabilities'
                    : 'Apply to join our exclusive partner network'}
                </p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              {[...Array(totalSteps)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${i + 1 <= currentStep ? 'bg-electric-blue' : 'bg-slate-gray/30'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/50">
          <div className="p-6 sm:p-8">
            <AnimatePresence mode="wait">
              {renderStep()}
            </AnimatePresence>

            {/* Submission error only (validation errors are shown inline next to each field) */}
            <div className="space-y-4 mb-6">
              {submissionError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-xl"
                  aria-live="polite"
                  role="alert"
                >
                  <p className="text-red-600 text-sm font-medium">{submissionError}</p>
                </motion.div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-gray/20 gap-4 sm:gap-0">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevStep}
                disabled={currentStep === 1}
                className="w-full sm:w-auto px-6 py-3 sm:py-4 border border-slate-gray/30 text-slate-gray font-medium rounded-xl hover:bg-light-gray disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 min-h-[48px] order-2 sm:order-1"
                aria-label="Previous step"
              >
                Previous
              </motion.button>

              {currentStep < totalSteps ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={nextStep}
                  disabled={isValidating}
                  className="w-full sm:w-auto px-6 py-3 sm:py-4 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center min-h-[48px] order-1 sm:order-2"
                >
                  {isValidating ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Validating...
                    </div>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                    </>
                  )}
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-success text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 min-h-[48px] order-1 sm:order-2"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {mode === 'edit'
                        ? 'Updating...'
                        : (isAdminCreatedProfile ? 'Updating Capabilities...' : 'Submitting...')}
                    </div>
                  ) : (
                    mode === 'edit'
                      ? 'Update Profile'
                      : (isAdminCreatedProfile ? 'Update Capabilities' : 'Submit Application')
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Admin Force Unclaim Confirmation Modal ── */}
      <AnimatePresence>
        {showForceUnclaimModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => !isForceUnclaiming && setShowForceUnclaimModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-gray/20"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 pt-6 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-charcoal">
                  {VALIDATION_MESSAGES.ADMIN_FORCE_UNCLAIM_CONFIRM_TITLE}
                </h3>
              </div>

              {/* Modal Body */}
              <div className="px-6 pb-4">
                <p className="text-slate-gray">
                  Are you sure you want to unclaim this CDMO profile? This will remove the current ownership of the profile.
                </p>
              </div>

              {/* Modal Footer */}
              <div className="px-6 pb-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowForceUnclaimModal(false)}
                  disabled={isForceUnclaiming}
                  className="px-5 py-2.5 border border-slate-gray/30 text-slate-gray font-medium rounded-xl hover:bg-light-gray disabled:opacity-50 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForceUnclaimConfirm}
                  disabled={isForceUnclaiming}
                  className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 transition-all duration-200 flex items-center gap-2"
                >
                  {isForceUnclaiming ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Unclaiming...
                    </>
                  ) : (
                    'Yes, Unclaim'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete FEI number confirmation modal ── */}
      <AnimatePresence>
        {deleteFeiIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => setDeleteFeiIndex(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-gray/20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-charcoal">
                  {VALIDATION_MESSAGES?.FEI_DELETE_CONFIRM_TITLE ?? 'Delete FEI number'}
                </h3>
              </div>
              <div className="px-6 pb-4">
                <p className="text-slate-gray">
                  {VALIDATION_MESSAGES?.FEI_DELETE_CONFIRM_MESSAGE ?? 'Are you sure you want to delete this FEI number?'}
                </p>
              </div>
              <div className="px-6 pb-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteFeiIndex(null)}
                  className="px-5 py-2.5 border border-slate-gray/30 text-slate-gray font-medium rounded-xl hover:bg-light-gray transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFeiConfirm}
                  className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-200"
                >
                  {VALIDATION_MESSAGES?.FEI_DELETE_CONFIRM_BUTTON ?? 'Yes, Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Permanent Claim Link Banner ── */}
      {/* <AnimatePresence>
        {permanentClaimLink && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-xl px-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-electric-blue/30 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal mb-1">🔗 Permanent Claim Link Generated</p>
                  <p className="text-xs text-slate-gray mb-3">Share this link with whoever should claim the profile. This link never expires.</p>
                  <div className="bg-light-gray rounded-lg px-3 py-2 font-mono text-xs text-charcoal break-all">
                    {permanentClaimLink}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(permanentClaimLink)
                      .then(() => showMessageModal(VALIDATION_MESSAGES.MODAL_TITLES.SUCCESS, VALIDATION_MESSAGES.CLAIM_LINK_COPIED, 'success'))
                      .catch(() => showMessageModal(VALIDATION_MESSAGES.MODAL_TITLES.ERROR, VALIDATION_MESSAGES.CLAIM_LINK_COPY_FAILED, 'error'));
                  }}
                  className="flex-1 px-4 py-2 bg-electric-blue text-white text-sm font-medium rounded-lg hover:bg-deep-indigo transition-colors"
                >
                  Copy Link
                </button>
                <button
                  onClick={() => setPermanentClaimLink(null)}
                  className="px-4 py-2 border border-slate-gray/30 text-slate-gray text-sm font-medium rounded-lg hover:bg-light-gray transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}

      {/* ── Admin Force Transfer Confirmation Modal ── */}
      <AnimatePresence>
        {showForceTransferModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => !isForceTransferring && setShowForceTransferModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-gray/20"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 pt-6 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-charcoal">
                  {VALIDATION_MESSAGES.ADMIN_FORCE_TRANSFER_CONFIRM_TITLE}
                </h3>
              </div>

              {/* Modal Body */}
              <div className="px-6 pb-4">
                <p className="text-slate-gray mb-4">
                  Are you sure you want to transfer ownership from <strong>{originalEmailRef.current}</strong> to <strong>{forceTransferNewEmail}</strong>?
                </p>
                <p className="text-slate-gray text-sm">
                  This will grant ownership to the new email and remove the current owner's access.
                </p>
              </div>

              {/* Modal Footer */}
              <div className="px-6 pb-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowForceTransferModal(false);
                    // Revert the email back to original
                    setFormData(prev => ({ ...prev, email: originalEmailRef.current }));
                  }}
                  disabled={isForceTransferring}
                  className="px-5 py-2.5 border border-slate-gray/30 text-slate-gray font-medium rounded-xl hover:bg-light-gray disabled:opacity-50 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForceTransferConfirm}
                  disabled={isForceTransferring}
                  className="px-5 py-2.5 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-all duration-200 flex items-center gap-2"
                >
                  {isForceTransferring ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Transferring...
                    </>
                  ) : (
                    'Confirm Transfer'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Message Modal */}
      <MessageModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
        onClose={() => {
          const onClose = messageModal.onClose;
          setMessageModal(prev => ({ ...prev, isOpen: false }));
          if (onClose) onClose();
        }}
      />
    </div>
  );
};

export { CDMOProfile };